import usePageFilterOptions from "../hooks/usePageFilterOptions";
import { loadFilterRecords } from "../utils/filterRecords";
import { buildContactListQuery, contactFilterPills } from "../utils/contactListQuery";
import ContactFiltersDrawer from "../components/ContactFiltersDrawer";
import { notifyCompanyRelationshipsChanged, resolveMergedCount, subscribeCompanyRelationshipsChanged } from "../utils/companyRelationshipCounts";
import CompanyTableCell from "../components/CompanyTableCell";
// src/pages/ClientsTable.jsx
import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { COMPANY_EXPORT_TAB_FIELDS } from "../utils/exportTabFields";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthController from "../hooks/useAuthController";
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  Stack,
  TableCell,
  Avatar,
  Divider,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";

import Breadcrumb from "../components/Breadcrumb";
import GenericDataTable from "../components/GenericDataTable";
import TopListHeaderBar from "../components/TopListHeaderBar";
import TabNavigationBar from "../components/TabNavigationBar";
import BulkActionsBar from "../components/BulkActionsBar";
import LeadTabsLayout from "../components/LeadTabsLayout";
import LeadDetailsView from "../components/LeadDetailsView";
import StatsSlider from "../components/StatsSlider";
import ReusableRightDrawer from "../components/ReusableRightDrawer";
import CompanyProfileDrawer from "../components/CompanyProfileDrawer";
import TableLocationValue from "../components/TableLocationValue";

// Advanced Search view component
import CompanyAdvancedSearchView from "../components/CompanyAdvancedSearchView";

// Template layout
import ListTableTabsTemplate from "../components/layouts/ListTableTabsTemplate";
import AddCompanyDialog from "../components/PopupWindows/AddCompanyDialog.jsx";
import AddToSequenceDialog from "../components/PopupWindows/AddToSequenceDialog.jsx";
import CsvImportDialog from "../components/CsvImportDialog";
import useCsvImport from "../hooks/useCsvImport";
import AddJobDialog from "../components/PopupWindows/AddJobDialog.jsx";
import AIAgentDialog from "../components/PopupWindows/AIAgentDialog.jsx";
import LeadDescriptionDialog from "../components/PopupWindows/LeadDescriptionDialog";
import { InlineBackgroundTaskLabel } from "../components/EntityBackgroundTasks";
import { syncCompanyBackgroundWork } from "../utils/entityBackgroundWorkState";
import InfoActionIcons from "../components/InfoActionIcons";
import { normalizeContactRoleLabel } from "../utils/contactRoles";
import AssignTeamMemberAction from "../components/AssignTeamMemberAction";
import { buildClientSequenceItem } from "../utils/sequenceItems";
import { fetchRecruiterUsers, fetchTeamMembers } from "../utils/teamMembers";
import {
  createSidebarResetState,
  getSidebarResetToken,
} from "../utils/sidebarNavigation";
import {
  buildCompanyProfileActions,
  getCompanySocialLinksArray,
} from "../utils/companySocialLinks";
import { areSamePersonNames } from "../utils/recruiterOwnership";
import { formatLocalDate } from "../utils/dateTime";
import { formatRelativeTimeShort, toTimestampValue } from "../utils/relativeTime";
import {
  getEntityLastActivityType,
  getCompanyLastActivityType,
  getEntityLastActivityValue,
  getEntityLastSyncValue,
} from "../utils/entityActivityTimestamps";
import {
  isHiringManagerContactRow,
  normalizeHiringManagerEntityType,
} from "../utils/hiringManagers";
import {
  ACTIVE_CLIENT_COMPANY_STATUS,
  COMPANY_STATUS_OPTIONS,
  isActiveClientCompanyStatus,
  normalizeCompanyStatusLabel,
} from "../utils/companyStatuses";
import PopupMultiSelectField, { popupFilterInputSx } from "../components/PopupMultiSelectField";
import LocationMultiSelectField from "../components/LocationMultiSelectField";
import { COMPANY_PROFILE_TABS, findTabLabel } from "../utils/breadcrumbs";
import {
  buildLocationOptions,
  extractRowLocations,
  formatLocationLabel,
  getEntityLocationValues,
  groupLocationOption,
  matchesLocationFilters,
  normalizeLocationFilterValues,
} from "../utils/locationFilters";
import { buildDetailFieldOptions } from "../utils/detailFieldOptions";
import {
  buildOpenCompanyNavEntity,
  buildOpenCompanyNavPayload,
} from "../utils/companyNavigation";
import { normalizeContactActionHref } from "../utils/contactChannels";
import {
  buildPublicSharePayload,
  createAndCopyPublicShareLink,
  SHARE_LINK_COPIED_MESSAGE,
} from "../utils/publicShareLinks";
import { ProposalActionIcon } from "../components/ActionButtonIcons";

// Iconsax (fix: Linkedin icon name is "Linkedin" NOT exported; use "Link" instead)
import { Filter } from "iconsax-react";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import { BoltOutlined as BoltOutlinedIcon } from "../components/icons/FigmaControlIcons";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PermContactCalendarOutlinedIcon from "@mui/icons-material/PermContactCalendarOutlined";

/* ---------- Nav payload cache key (shared with Leads page drawer) ---------- */
const NAV_CACHE_KEY = "rpo:openCompanyNav";

const LIST_TABS = {
  clients: "clients",
  companies: "companies",
  contacts: "contacts",
};
const BULK_ACTION_RIGHT_BUTTON_COLOR = "#9C4FFF";
const BULK_ACTION_RIGHT_BUTTON_SX = {
  background: `${BULK_ACTION_RIGHT_BUTTON_COLOR} !important`,
  backgroundColor: `${BULK_ACTION_RIGHT_BUTTON_COLOR} !important`,
  color: "#fff !important",
  borderColor: `${BULK_ACTION_RIGHT_BUTTON_COLOR} !important`,
  "&:hover": {
    background: `${BULK_ACTION_RIGHT_BUTTON_COLOR} !important`,
    backgroundColor: `${BULK_ACTION_RIGHT_BUTTON_COLOR} !important`,
    color: "#fff !important",
    borderColor: `${BULK_ACTION_RIGHT_BUTTON_COLOR} !important`,
  },
  "&:active": {
    background: `${BULK_ACTION_RIGHT_BUTTON_COLOR} !important`,
    backgroundColor: `${BULK_ACTION_RIGHT_BUTTON_COLOR} !important`,
    color: "#fff !important",
    borderColor: `${BULK_ACTION_RIGHT_BUTTON_COLOR} !important`,
  },
  "&:focus-visible, &.Mui-focusVisible": {
    background: `${BULK_ACTION_RIGHT_BUTTON_COLOR} !important`,
    backgroundColor: `${BULK_ACTION_RIGHT_BUTTON_COLOR} !important`,
    color: "#fff !important",
    borderColor: `${BULK_ACTION_RIGHT_BUTTON_COLOR} !important`,
    outline: `3px solid ${BULK_ACTION_RIGHT_BUTTON_COLOR}`,
  },
};
const hasRenderableActionHref = (type, href) =>
  Boolean(normalizeContactActionHref(type, href));

const buildTableInfoActions = (company = {}) => {
  const actions = buildCompanyProfileActions(company);
  const linkedinAction = actions.find(
    (action) =>
      action?.type === "linkedin" &&
      hasRenderableActionHref(action?.type || "website", action?.href)
  );

  return [
    ...actions.filter((action) => ["website", "email", "phone"].includes(action?.type)),
    ...(linkedinAction ? [linkedinAction] : []),
  ];
};

const buildContactInfoActions = (contact = {}) => [
  { type: "email", href: contact?.email || contact?.contact_email || "" },
  { type: "linkedin", href: contact?.linkedin_url || contact?.linkedin || "" },
  { type: "phone", href: contact?.phone || contact?.contact_phone || "" },
];
const DEFAULT_LIST_TAB = LIST_TABS.clients;
const SIDEBAR_HOME_LIST_TAB = LIST_TABS.companies;
const getRowLastSyncValue = (row = {}) => getEntityLastSyncValue(row);
const getRowLastActivityType = (row = {}) => getEntityLastActivityType(row);
const getRowLastActivityValue = (row = {}) => getEntityLastActivityValue(row);
const getRowCreatedTimeValue = (row = {}) => row.created_at || row.createdAt || "";
const getCompanyRowLastSyncValue = (row = {}) =>
  getRowLastSyncValue(row) || getRowCreatedTimeValue(row);
const getCompanyRowCreatedFallbackValue = (row = {}) => getRowCreatedTimeValue(row);
const getCompanyRowLastActivityValue = (row = {}) =>
  getRowLastActivityValue(row) || getCompanyRowCreatedFallbackValue(row);
const getCompanyRowLastActivityType = getCompanyLastActivityType;
const buildListLastActivityFields = (row = {}, { companyEntity = false } = {}) => {
  const lastActivityValue = companyEntity
    ? getCompanyRowLastActivityValue(row)
    : getRowLastActivityValue(row);
  const lastActivityType = companyEntity
    ? getCompanyRowLastActivityType(row)
    : getRowLastActivityType(row) || "-";

  return {
    lastActivity: formatRelativeTimeShort(lastActivityValue, { emptyValue: "-" }),
    lastActivityAt: toTimestampValue(lastActivityValue),
    lastActivityType,
    last_activity_at: toTimestampValue(lastActivityValue),
    last_activity_time: lastActivityValue || "",
    last_activity_type: String(
      row?.last_activity_type || row?.lastActivityType || row?.lastActivity || lastActivityType || ""
    ).trim(),
  };
};
const normalizeInlineText = (value) => String(value ?? "").replace(/\s+/g, " ").trim();
const normalizeInlineTextLower = (value) => normalizeInlineText(value).toLowerCase();
const COMPANY_STATUS_BADGE_STYLES = {
  New: { backgroundColor: "#EEF4FF", color: "#3538CD" },
  Contacted: { backgroundColor: "#ECFDF3", color: "#027A48" },
  Discovery: { backgroundColor: "#FFF7E8", color: "#B54708" },
  Proposal: { backgroundColor: "#F4F3FF", color: "#6941C6" },
  "Active Client": { backgroundColor: "#DCFCE7", color: "#15803D" },
  "Do Not Contact": { backgroundColor: "#FEE4E2", color: "#B42318" },
  default: { backgroundColor: "#F2F4F7", color: "#475467" },
};

function getPersonInitials(value) {
  const text = normalizeInlineText(value);
  if (!text) return "?";

  const parts = text.split(" ").filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function getCompanyStatusBadgeSx(status) {
  const palette =
    COMPANY_STATUS_BADGE_STYLES[normalizeInlineText(status)] ||
    COMPANY_STATUS_BADGE_STYLES.default;

  return {
    height: 22,
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    border: "none",
    backgroundColor: palette.backgroundColor,
    color: palette.color,
    "& .MuiChip-label": {
      px: 0.95,
    },
  };
}

function getClientPortalBadgeProps(status) {
  const enabled = isActiveClientCompanyStatus(status);

  return enabled
    ? {
        label: "Enabled",
        sx: {
          height: 22,
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 700,
          border: "none",
          backgroundColor: "#DCFCE7",
          color: "#15803D",
          "& .MuiChip-label": { px: 0.95 },
        },
      }
    : {
        label: "Disabled",
        sx: {
          height: 22,
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 700,
          border: "none",
          backgroundColor: "#F2F4F7",
          color: "#475467",
          "& .MuiChip-label": { px: 0.95 },
        },
      };
}

function isAssignedCompanyTeamContact(contact = {}) {
  const combined = normalizeInlineTextLower(
    [
      contact?.contact_kind,
      contact?.role,
      contact?.title,
      contact?.position,
      contact?.source,
    ]
      .filter(Boolean)
      .join(" ")
  );

  return [
    "recruiter",
    "sourcer",
    "account manager",
    "recruiting manager",
    "owner",
    "team member",
    "team_member",
  ].some((token) => combined.includes(token));
}

function buildAssignedTeamMembers(row = {}, teamDirectoryLookup = new Map()) {
  const nextMembers = [];
  const seen = new Set();

  const pushMember = (name, avatarUrl = "") => {
    const normalizedName = normalizeInlineText(name);
    if (!normalizedName || normalizedName === "-") return;

    const key = normalizedName.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);

    const lookup = teamDirectoryLookup.get(key);
    nextMembers.push({
      key,
      name: normalizedName,
      avatarUrl: normalizeInlineText(avatarUrl || lookup?.avatar_url),
    });
  };

  (Array.isArray(row?.contacts) ? row.contacts : []).forEach((contact) => {
    if (!isAssignedCompanyTeamContact(contact)) return;
    pushMember(contact?.name, contact?.profile_image || contact?.avatar_url || contact?.avatar);
  });

  pushMember(row?.team_member || row?.owner_name || row?.owner);
  return nextMembers;
}

function escapeCsvCell(value) {
  const text = String(value ?? "");
  if (!/[",\r\n]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadCsvFile(filename, rows = []) {
  if (!rows.length || typeof window === "undefined") return;

  const csv = rows.map((row) => row.map(escapeCsvCell).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}

function mapLeadRowToListRow(row = {}) {
  const lastSyncValue = getRowLastSyncValue(row);
  const lastActivityValue = getRowLastActivityValue(row);
  const lastActivityType = getRowLastActivityType(row);
  const structuredLocation =
    formatLocationLabel(row.location_city, row.location_state, row.location_country) || "";

  return {
    id: row.id,
    entityType: "lead",
    name: row.company_name || row.name || "",
    logo: row.company_logo || row.logo || "",
    website: row.website || row.career_website || "",
    website_url: row.website || row.career_website || "",
    status: row.status || "No stage",
    jobOpenings:
      typeof row.job_openings === "number"
        ? row.job_openings
        : Number(row.job_openings || row.jobOpenings || 0),
    location: structuredLocation || row.location || "",
    contact:
      row.contact_name ||
      row.contact_email ||
      row.contact_phone ||
      "-",
    source: row.source || "-",
    linkedin_url: row.linkedin_url || "",
    facebook_url: row.facebook_url || "",
    twitter_url: row.twitter_url || row.x_url || "",
    instagram_url: row.instagram_url || "",
    youtube_url: row.youtube_url || "",
    tiktok_url: row.tiktok_url || "",
    contact_email: row.contact_email || "",
    contact_phone: row.contact_phone || "",
    social_links: getCompanySocialLinksArray(row),
    lastSync: formatRelativeTimeShort(lastSyncValue, { emptyValue: "-" }),
    lastSyncAt: toTimestampValue(lastSyncValue),
    ...buildListLastActivityFields({
      ...row,
      lastActivityType,
      last_activity_type: row.last_activity_type || lastActivityType,
      last_activity_time: lastActivityValue,
    }),
    owner: row.owner_name || row.owner || "-",
    created_at: row.created_at,
    industry: row.industry || row.industry_sector || "",
    hard_skills: row.hard_skills || row.tech_stack || row.skills || "",
    soft_skills: row.soft_skills || "",
    technology_stack: row.technology_stack || row.tech_stack || "",
    recruitment_tools: row.recruitment_tools || "",
    salary_range: row.salary_range || row.compensation || "",
    compensation: row.compensation || row.salary_range || "",
    leadsCount: Number(row.leadsCount ?? row.leads_count ?? 0),
    contactsCount: Number(row.contactsCount ?? row.contacts_count ?? 0),
    candidatesCount: Number(row.candidatesCount ?? row.candidates_count ?? 0),
    activitiesCount: Number(row.activitiesCount ?? row.activities_count ?? 0),
    related_leads: Array.isArray(row.related_leads) ? row.related_leads : undefined,
    contacts: Array.isArray(row.contacts) ? row.contacts : undefined,
    jobs: Array.isArray(row.jobs) ? row.jobs : undefined,
    activities: Array.isArray(row.activities) ? row.activities : undefined,
    full_company_description: row.full_company_description || "",
    notes: row.notes || "",
    raw: row,
  };
}

export function mapCompanyRowToListRow(row = {}) {
  const lastSyncValue = getCompanyRowLastSyncValue(row);
  const lastActivityValue = getCompanyRowLastActivityValue(row);
  const lastActivityType = getCompanyRowLastActivityType(row);
  const structuredLocation =
    formatLocationLabel(row.location_city, row.location_state, row.location_country) || "";
  // Keep the complete list on the table row itself.  The compact cell renders
  // the first location plus its count, while the same row can safely open a
  // profile without losing locations_json to its primary `location` field.
  const locations = getEntityLocationValues({
    ...row,
    location: structuredLocation || row.location,
  });
  const jobsCount = Number(row.jobsCount ?? row.jobs_count ?? 0) || 0;
  const leadsCount = Number(row.leadsCount ?? row.leads_count ?? 0) || 0;

  return {
    id: row.id,
    entityType: "company",
    name: row.name || row.company_name || "",
    logo: row.logo || row.company_logo || "",
    website: row.website || row.company_link || row.career_site || "",
    website_url: row.website || row.company_link || row.career_site || "",
    status: normalizeCompanyStatusLabel(row.status, { fallback: "New" }),
    jobsCount,
    jobs_count: jobsCount,
    jobOpenings: jobsCount,
    job_openings: jobsCount,
    location: structuredLocation || row.location || locations[0] || "",
    locations,
    locations_json: locations,
    locationsJson: locations,
    location_values: locations,
    contact: row.job_primary_contact_name || row.contact_name || "-",
    source: row.source || "-",
    linkedin_url: row.linkedin_url || "",
    facebook_url: row.facebook_url || "",
    twitter_url: row.twitter_url || row.x_url || "",
    instagram_url: row.instagram_url || "",
    youtube_url: row.youtube_url || "",
    tiktok_url: row.tiktok_url || "",
    contact_email: row.job_primary_contact_email || row.contact_email || row.email || "",
    contact_phone: row.job_primary_contact_phone || row.contact_phone || row.phone || "",
    social_links: getCompanySocialLinksArray(row),
    lastSync: formatRelativeTimeShort(lastSyncValue, { emptyValue: "-" }),
    lastSyncAt: toTimestampValue(lastSyncValue),
    ...buildListLastActivityFields(
      {
        ...row,
        lastActivityType,
        last_activity_type: row.last_activity_type || lastActivityType,
        last_activity_time: lastActivityValue,
      },
      { companyEntity: true }
    ),
    owner: row.team_member || row.owner_name || row.owner || "-",
    created_at: row.created_at,
    industry: row.industry || row.industry_sector || "",
    hard_skills: row.hard_skills || row.tech_stack || row.skills || "",
    soft_skills: row.soft_skills || "",
    technology_stack: row.technology_stack || row.tech_stack || "",
    recruitment_tools: row.recruitment_tools || "",
    salary_range: row.salary_range || row.compensation || "",
    compensation: row.compensation || row.salary_range || "",
    leadsCount,
    leads_count: leadsCount,
    contactsCount: Number(row.contactsCount ?? row.contacts_count ?? 0),
    candidatesCount: Number(row.candidatesCount ?? row.candidates_count ?? 0),
    activitiesCount: Number(row.activitiesCount ?? row.activities_count ?? 0),
    contact_repair_pending: Boolean(row.contact_repair_pending),
    company_profile_enrichment_pending: Boolean(row.company_profile_enrichment_pending),
    related_leads: Array.isArray(row.related_leads) ? row.related_leads : undefined,
    contacts: Array.isArray(row.contacts) ? row.contacts : undefined,
    jobs: Array.isArray(row.jobs) ? row.jobs : undefined,
    activities: Array.isArray(row.activities) ? row.activities : undefined,
    full_company_description: row.full_company_description || row.notes || "",
    notes: row.notes || "",
    raw: {
      ...row,
      locations,
      locations_json: locations,
      locationsJson: locations,
      location_values: locations,
    },
  };
}

/* ------------------ Filters (local filtering) ------------------ */
const DEFAULT_FILTERS = {
  ai: "",
  companyNames: [],
  techKeywords: [],
  locations: [],
  owners: [],
  statuses: [],
  activities: [],
  sources: [],
};

const norm = (v) => String(v ?? "").replace(/\s+/g, " ").toLowerCase().trim();
const cleanFilterValue = (value) => String(value ?? "").trim();
const sameFilterValue = (left, right) => norm(left) === norm(right);
const isClientRow = (row = {}) => isActiveClientCompanyStatus(row?.status);

export function getCompanyTabRows(rows, tab) {
  return tab === LIST_TABS.clients ? rows.filter(isClientRow) : rows;
}
const removeFilterValueFromList = (values = [], target) =>
  (values || [])
    .filter((value) => !sameFilterValue(value, target))
    .map(cleanFilterValue)
    .filter(Boolean);
const SALES_SEQUENCE_ENTITY_TYPES = new Set(["client", "company", "lead"]);
const normalizeSequenceEntityType = (value) => String(value ?? "").trim().toLowerCase();
const hasAssignedOwner = (row = {}) => {
  const owner = cleanFilterValue(row?.owner || row?.owner_name || row?.team_member || "");
  return Boolean(owner) && owner !== "-";
};
const getRowJobOpeningsCount = (row = {}) =>
  Math.max(
    0,
    Number(row?.jobsCount ?? row?.jobs_count ?? 0) || 0
  );
const matchesCompanyPipelineStatus = (row = {}, statusLabel = "") =>
  sameFilterValue(
    normalizeCompanyStatusLabel(row?.status, {
      fallback: "",
      preserveUnknown: true,
    }),
    statusLabel
  );
const buildSalesSequenceEntityKeys = (entityType, entityId) => {
  const normalizedType =
    normalizeHiringManagerEntityType(entityType) || normalizeSequenceEntityType(entityType);
  const normalizedId = cleanFilterValue(entityId);
  if (!normalizedId) return [];

  if (normalizedType === "company" || normalizedType === "client") {
    return [`client:${normalizedId}`, `company:${normalizedId}`];
  }

  if (normalizedType === "lead") {
    return [`lead:${normalizedId}`];
  }

  return [];
};
const COMPANY_AGENT_TASK_OPTIONS = [
  { value: "findContacts", label: "Find Contacts" },
  { value: "findHiringManagers", label: "Find Hiring Managers" },
  { value: "discoverJobs", label: "Discover Jobs" },
  { value: "companyResearch", label: "Company Research" },
  { value: "createProposal", label: "Create Proposal" },
  { value: "convertToClient", label: "Convert to Client" },
];
const formatCompanyAgentTaskRow = (row = {}, index = 0) =>
  `${index + 1}. ${cleanFilterValue(row?.name) || "Unknown Company"}${
    cleanFilterValue(row?.website) ? ` | Website: ${cleanFilterValue(row.website)}` : ""
  }${cleanFilterValue(row?.location) ? ` | Location: ${cleanFilterValue(row.location)}` : ""}${
    cleanFilterValue(row?.status) ? ` | Status: ${cleanFilterValue(row.status)}` : ""
  }`;
function buildCompanyAgentTaskPrompt({ taskValue = "", rows = [] } = {}) {
  const selectedRows = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const selectionBlock = selectedRows.length
    ? selectedRows.map((row, index) => formatCompanyAgentTaskRow(row, index)).join("\n")
    : "No companies selected.";

  switch (String(taskValue || "").trim()) {
    case "findContacts":
      return [
        "For each selected company, find public business contacts and decision-makers.",
        "Return structured results with company name, contact name, role, public profile or website URL, source URL, and confidence notes.",
        "Do not invent direct contact information.",
        "",
        "Selected companies:",
        selectionBlock,
      ].join("\n");
    case "findHiringManagers":
      return [
        "For each selected company, find likely hiring managers or talent leaders tied to current hiring activity.",
        "Return structured results with company name, hiring manager name, role, public profile URL, source URL, and why they are relevant.",
        "Do not invent direct contact information.",
        "",
        "Selected companies:",
        selectionBlock,
      ].join("\n");
    case "discoverJobs":
      return [
        "Find current public job openings for each selected company.",
        "Return role title, location, source site, job URL, and whether the opening appears active.",
        "",
        "Selected companies:",
        selectionBlock,
      ].join("\n");
    case "companyResearch":
      return [
        "Research each selected company for RPO sales and delivery context.",
        "Include a short summary, industry, hiring signals, funding or growth notes, notable leadership, and recruiting opportunities.",
        "",
        "Selected companies:",
        selectionBlock,
      ].join("\n");
    case "createProposal":
      return [
        "Create a concise recruiting proposal for each selected company.",
        "Include likely pain points, suggested engagement model, sourcing plan, candidate profile angles, and a short outreach-ready summary.",
        "",
        "Selected companies:",
        selectionBlock,
      ].join("\n");
    default:
      return [
        "Help me with the selected companies.",
        "",
        "Selected companies:",
        selectionBlock,
      ].join("\n");
  }
}

/**
 * Normalize drawer filter payloads
 */
function normalizeFilters(input) {
  const f = input || {};

  const pick = (...keys) => {
    for (const k of keys) {
      if (f[k] !== undefined && f[k] !== null) return f[k];
    }
    return undefined;
  };

  const toStringArray = (v) => {
    if (!v) return [];
    const arr = Array.isArray(v) ? v : [v];
    return arr
      .map((x) => {
        if (x === null || x === undefined) return "";
        if (typeof x === "string" || typeof x === "number") return String(x);
        if (typeof x === "object") return String(x.label ?? x.value ?? x.name ?? "");
        return "";
      })
      .map((s) => s.trim())
      .filter(Boolean);
  };

  return {
    ai: String(pick("ai", "query", "search", "text") ?? ""),
    companyNames: toStringArray(pick("companyNames", "companyName", "company_names", "companies", "company")),
    techKeywords: toStringArray(pick("techKeywords", "tech", "keywords", "tech_keywords")),
    locations: toStringArray(pick("locations", "location", "cities", "countries")),
    owners: toStringArray(pick("owners", "owner", "teamMembers", "team_member")),
    statuses: toStringArray(pick("statuses", "status", "stages")),
    activities: toStringArray(pick("activities", "activity", "lastActivities", "last_activity")),
    sources: toStringArray(pick("sources", "source", "leadSources", "lead_source")),
  };
}

export function applyCompanyFilters(rows, filtersRaw) {
  const filters = normalizeFilters(filtersRaw);

  const ai = norm(filters.ai);
  const companyNames = (filters.companyNames || []).map(norm).filter(Boolean);
  const techKeywords = (filters.techKeywords || []).map(norm).filter(Boolean);
  const locations = (filters.locations || []).map(norm).filter(Boolean);
  const owners = (filters.owners || []).map(norm).filter(Boolean);
  const statuses = (filters.statuses || []).map(norm).filter(Boolean);
  const activities = (filters.activities || []).map(norm).filter(Boolean);
  const sources = (filters.sources || []).map(norm).filter(Boolean);

  const hasAny =
    ai ||
    companyNames.length ||
    techKeywords.length ||
    locations.length ||
    owners.length ||
    statuses.length ||
    activities.length ||
    sources.length;

  if (!hasAny) return rows;

  return rows.filter((r) => {
    const name = norm(r.name);
    const website = norm(r.website);
    const status = norm(r.status);
    const location = norm(r.location);
    const contact = norm(r.contact);
    const source = norm(r.source);
    const owner = norm(r.owner);
    const lastActivity = norm(r.lastActivityType || r.lastActivity);
    const industry = norm(r.industry);
    const hardSkills = norm(buildDetailFieldOptions([r]).skills.join(" "));
    const softSkills = norm(r.soft_skills);
    const salary = norm(r.salary_range || r.compensation);

    const blob = [
      name,
      website,
      status,
      location,
      contact,
      source,
      owner,
      lastActivity,
      industry,
      hardSkills,
      softSkills,
      salary,
    ].join(" ");

    if (ai && !blob.includes(ai)) return false;

    if (companyNames.length && !companyNames.some((x) => name.includes(x))) return false;
    if (owners.length && !owners.some((x) => owner.includes(x))) return false;
    if (statuses.length && !statuses.some((x) => status.includes(x))) return false;
    if (activities.length && !activities.some((x) => lastActivity.includes(x))) return false;
    if (sources.length && !sources.some((x) => source.includes(x))) return false;
    if (locations.length && !matchesLocationFilters(r, filters.locations)) return false;

    if (techKeywords.length && !techKeywords.every((kw) => blob.includes(kw))) return false;

    return true;
  });
}

function hasBasicCompanyFilters(filtersRaw) {
  const filters = normalizeFilters(filtersRaw);

  return Boolean(
    norm(filters.ai) ||
      (filters.companyNames || []).length ||
      (filters.techKeywords || []).length ||
      (filters.locations || []).length ||
      (filters.owners || []).length ||
      (filters.statuses || []).length ||
      (filters.activities || []).length ||
      (filters.sources || []).length
  );
}

/* Advanced Search (local filtering) */
function applyAdvancedCompanyFilters(rows, adv) {
  if (!adv) return rows;

  const ai = norm(adv.aiAgent);
  const companyName = norm(adv.companyName);
  const skills = (adv.skills || []).map(norm).filter(Boolean);
  const locations = (adv.locations || []).map(norm).filter(Boolean);
  const owner = (adv.owner || []).map(norm).filter(Boolean);
  const status = (adv.status || []).map(norm).filter(Boolean);
  const activity = (adv.activity || []).map(norm).filter(Boolean);
  const source = (adv.source || []).map(norm).filter(Boolean);

  const cd = adv.company_details || {};
  const cdIndustry = (cd.industry || []).map(norm).filter(Boolean);
  const cdCompanyType = (cd.company_type || []).map(norm).filter(Boolean);

  const hasAny =
    ai ||
    companyName ||
    skills.length ||
    locations.length ||
    owner.length ||
    status.length ||
    activity.length ||
    source.length ||
    cdIndustry.length ||
    cdCompanyType.length;

  if (!hasAny) return rows;

  return rows.filter((r) => {
    const name = norm(r.name);
    const website = norm(r.website);
    const rStatus = norm(r.status);
    const rLocation = norm(r.location);
    const rContact = norm(r.contact);
    const rSource = norm(r.source);
    const rOwner = norm(r.owner);
    const rLastActivity = norm(r.lastActivityType || r.lastActivity);
    const rIndustry = norm(r.industry || "");
    const rHardSkills = norm(r.hard_skills || r.technology_stack || r.skills || r.recruitment_tools);
    const rSoftSkills = norm(r.soft_skills || "");
    const rSalary = norm(r.salary_range || r.compensation || "");

    const blob = [
      name,
      website,
      rStatus,
      rLocation,
      rContact,
      rSource,
      rOwner,
      rLastActivity,
      rIndustry,
      rHardSkills,
      rSoftSkills,
      rSalary,
    ].join(" ");

    if (ai && !blob.includes(ai)) return false;
    if (companyName && !name.includes(companyName)) return false;
    if (skills.length && !skills.every((kw) => blob.includes(kw))) return false;
    if (locations.length && !matchesLocationFilters(r, adv.locations)) return false;
    if (owner.length && !owner.some((x) => rOwner.includes(x))) return false;
    if (status.length && !status.some((x) => rStatus.includes(x))) return false;
    if (activity.length && !activity.some((x) => rLastActivity.includes(x))) return false;
    if (source.length && !source.some((x) => rSource.includes(x))) return false;

    if (cdIndustry.length && !cdIndustry.some((x) => rIndustry.includes(x) || blob.includes(x))) return false;

    if (cdCompanyType.length && !cdCompanyType.some((x) => blob.includes(x))) return false;

    return true;
  });
}

function hasAdvancedCompanyFilters(adv) {
  if (!adv) return false;

  const ai = norm(adv.aiAgent);
  const companyName = norm(adv.companyName);
  const skills = (adv.skills || []).map(norm).filter(Boolean);
  const locations = (adv.locations || []).map(norm).filter(Boolean);
  const owner = (adv.owner || []).map(norm).filter(Boolean);
  const status = (adv.status || []).map(norm).filter(Boolean);
  const activity = (adv.activity || []).map(norm).filter(Boolean);
  const source = (adv.source || []).map(norm).filter(Boolean);

  const cd = adv.company_details || {};
  const cdIndustry = (cd.industry || []).map(norm).filter(Boolean);
  const cdCompanyType = (cd.company_type || []).map(norm).filter(Boolean);

  return Boolean(
    ai ||
      companyName ||
      skills.length ||
      locations.length ||
      owner.length ||
      status.length ||
      activity.length ||
      source.length ||
      cdIndustry.length ||
      cdCompanyType.length
  );
}

/* ------------------ URL tab normalization ------------------ */
const normalizeTab = (t) => {
  const v = String(t || "").toLowerCase();
  const ok = ["details", "leads", "contacts", "jobs", "candidates", "activities"];
  return ok.includes(v) ? v : "details";
};

const normalizeLeadDetailTab = (value) => {
  const normalized = String(value || "").toLowerCase();
  const validTabs = ["details", "stages", "matches", "activities"];
  return validTabs.includes(normalized) ? normalized : "details";
};

const mapLeadRowToSelectedCompany = (row) => ({
  ...(row || {}),
  id: (() => {
    const resolvedLeadId = Number(row?.lead_id ?? row?.leadId ?? row?.id ?? 0);
    return Number.isFinite(resolvedLeadId) && resolvedLeadId > 0 ? resolvedLeadId : row?.id;
  })(),
  entityType: "lead",
  name: row.name || row.company_name || "",
  website: row.website || row.website_url || row.career_website || "",
  industry: row.industry || row.industry_sector || "Technology",
  status: row.status,
  owner: row.owner || row.owner_name || "",
  createdAt: formatLocalDate(row.created_at, {}, "-"),
  lastActivity: row.lastActivityType || row.last_activity_type || row.lastActivity || "",
  lastActivityDetail: `Last activity: ${row.lastActivityType || row.last_activity_type || row.lastActivity || "-"} via ${row.source || "-"}`,
  lastActivityAt: toTimestampValue(getRowLastActivityValue(row)),
  last_activity_at: toTimestampValue(getRowLastActivityValue(row)),
  last_activity_time: getRowLastActivityValue(row),
  jobsCount: Number(row.jobsCount ?? row.jobs_count ?? row.jobOpenings ?? row.job_openings ?? 0),
  jobs_count: Number(row.jobsCount ?? row.jobs_count ?? row.jobOpenings ?? row.job_openings ?? 0),
  jobOpenings: Number(row.jobsCount ?? row.jobs_count ?? row.jobOpenings ?? row.job_openings ?? 0),
  job_openings: Number(row.jobsCount ?? row.jobs_count ?? row.jobOpenings ?? row.job_openings ?? 0),
  source: row.source,
  logo: row.logo || row.company_logo || "",
  location:
    formatLocationLabel(row.location_city, row.location_state, row.location_country) ||
    row.location ||
    "",
  lastSync:
    row.lastSync ||
    formatRelativeTimeShort(getRowLastSyncValue(row), { emptyValue: "" }),
  lastActivityType: row.lastActivityType || row.last_activity_type || row.lastActivity || "",
  leadsCount: Number(row.leadsCount ?? row.leads_count ?? 0),
  contactsCount: Number(row.contactsCount ?? row.contacts_count ?? 0),
  candidatesCount: Number(row.candidatesCount ?? row.candidates_count ?? 0),
  activitiesCount: Number(row.activitiesCount ?? row.activities_count ?? 0),
  related_leads: Array.isArray(row.related_leads) ? row.related_leads : undefined,
  contacts: Array.isArray(row.contacts) ? row.contacts : undefined,
  jobs: Array.isArray(row.jobs) ? row.jobs : undefined,
  contact_email: row.contact_email || row.email || "",
  contact_phone: row.contact_phone || row.phone || "",
  linkedin_url: row.linkedin_url || "",
  facebook_url: row.facebook_url || "",
  twitter_url: row.twitter_url || row.x_url || "",
  instagram_url: row.instagram_url || "",
  youtube_url: row.youtube_url || "",
  tiktok_url: row.tiktok_url || "",
  social_links: getCompanySocialLinksArray(row),
});

const mapCompanyRowToSelectedCompany = (row) => {
  const sourceRow = row || {};
  // Normalize locations at the list-to-profile boundary.  This protects the
  // profile from partial table rows and keeps the edit form, details view, and
  // searchable location controls in sync.
  const locations = getEntityLocationValues(sourceRow);

  return {
  ...sourceRow,
  id: row.id,
  entityType: "company",
  name: row.name || row.company_name || "",
  website: row.website || row.website_url || row.company_link || row.career_site || "",
  industry: row.industry || row.industry_sector || "Technology",
  status: normalizeCompanyStatusLabel(row.status, { fallback: "New" }),
  owner: row.owner || row.owner_name || row.team_member || "",
  owner_name: row.owner_name || row.owner || row.team_member || "",
  createdAt: formatLocalDate(row.created_at, {}, "-"),
  lastActivity: row.lastActivityType || row.last_activity_type || row.lastActivity || "",
  lastActivityDetail: row.notes || row.full_company_description || "",
  lastActivityAt: toTimestampValue(getCompanyRowLastActivityValue(row)),
  last_activity_at: toTimestampValue(getCompanyRowLastActivityValue(row)),
  last_activity_time: getCompanyRowLastActivityValue(row),
  jobsCount: Number(row.jobsCount ?? row.jobs_count ?? 0),
  jobs_count: Number(row.jobsCount ?? row.jobs_count ?? 0),
  jobOpenings: Number(row.jobsCount ?? row.jobs_count ?? 0),
  job_openings: Number(row.jobsCount ?? row.jobs_count ?? 0),
  source: row.source,
  logo: row.logo || row.company_logo || "",
  location:
    formatLocationLabel(sourceRow.location_city, sourceRow.location_state, sourceRow.location_country) ||
    sourceRow.location ||
    locations[0] ||
    "",
  locations,
  locations_json: locations,
  locationsJson: locations,
  location_values: locations,
  lastSync:
    row.lastSync ||
    formatRelativeTimeShort(getCompanyRowLastSyncValue(row), { emptyValue: "" }),
  lastActivityType:
    row.lastActivityType || row.last_activity_type || row.lastActivity || getCompanyRowLastActivityType(row),
  leadsCount: Number(row.leadsCount ?? row.leads_count ?? 0),
  contactsCount: Number(row.contactsCount ?? row.contacts_count ?? 0),
  candidatesCount: Number(row.candidatesCount ?? row.candidates_count ?? 0),
  activitiesCount: Number(row.activitiesCount ?? row.activities_count ?? 0),
  related_leads: Array.isArray(row.related_leads) ? row.related_leads : undefined,
  contacts: Array.isArray(row.contacts) ? row.contacts : undefined,
  jobs: Array.isArray(row.jobs) ? row.jobs : undefined,
  contact_email: row.contact_email || row.email || "",
  contact_phone: row.contact_phone || row.phone || "",
  linkedin_url: row.linkedin_url || "",
  facebook_url: row.facebook_url || "",
  twitter_url: row.twitter_url || row.x_url || "",
  instagram_url: row.instagram_url || "",
  youtube_url: row.youtube_url || "",
  tiktok_url: row.tiktok_url || "",
  social_links: getCompanySocialLinksArray(row),
  notes: row.notes || "",
  company_description: row.company_description || row.full_company_description || row.notes || "",
  full_company_description: row.full_company_description || row.notes || "",
  elevator_pitch: row.elevator_pitch || "",
  mission_and_vision: row.mission_and_vision || "",
  key_products_services: row.key_products_services || "",
  unique_selling_points: row.unique_selling_points || "",
  employee_perks: row.employee_perks || "",
  achievements_awards: row.achievements_awards || "",
  company_profile_enrichment_pending: Boolean(row.company_profile_enrichment_pending),
  };
};

const SELECTED_COMPANY_STABLE_KEYS = [
  "id",
  "entityType",
  "name",
  "company_name",
  "status",
  "owner",
  "owner_name",
  "website",
  "website_url",
  "company_website",
  "logo",
  "company_logo",
  "location",
  "source",
  "lastActivity",
  "lastActivityType",
  "last_activity_time",
  "last_activity_type",
  "last_activity_message",
  "notes",
  "company_description",
  "full_company_description",
  "elevator_pitch",
  "mission_and_vision",
  "key_products_services",
  "unique_selling_points",
  "employee_perks",
  "achievements_awards",
  "leadsCount",
  "leads_count",
  "contactsCount",
  "contacts_count",
  "jobsCount",
  "jobs_count",
  "jobOpenings",
  "job_openings",
  "candidatesCount",
  "candidates_count",
  "activitiesCount",
  "activities_count",
  "contact_repair_pending",
  "company_profile_enrichment_pending",
];

function reuseSelectedCompanyIfUnchanged(previousCompany, nextCompany) {
  if (!previousCompany || !nextCompany) return nextCompany;
  if (previousCompany === nextCompany) return previousCompany;

  const stableScalarsMatch = SELECTED_COMPANY_STABLE_KEYS.every(
    (key) => previousCompany?.[key] === nextCompany?.[key]
  );

  if (!stableScalarsMatch) return nextCompany;

  const stableArraysMatch =
    previousCompany?.contacts === nextCompany?.contacts &&
    previousCompany?.related_leads === nextCompany?.related_leads &&
    previousCompany?.activities === nextCompany?.activities &&
    previousCompany?.jobs === nextCompany?.jobs &&
    previousCompany?.candidates === nextCompany?.candidates &&
    previousCompany?.hiringManagers === nextCompany?.hiringManagers &&
    previousCompany?.hiring_managers === nextCompany?.hiring_managers;

  return stableArraysMatch ? previousCompany : nextCompany;
}

const mergeSelectedCompanyFromListRow = (previousCompany, nextRow) => {
  const mappedCompany = mapCompanyRowToSelectedCompany(nextRow);
  if (!previousCompany || String(previousCompany?.id || "") !== String(mappedCompany?.id || "")) {
    return mappedCompany;
  }

  const getArrayOrEmpty = (value) => (Array.isArray(value) ? value : []);
  const hasNextContacts = Array.isArray(mappedCompany.contacts);
  const hasNextRelatedLeads = Array.isArray(mappedCompany.related_leads);
  const hasNextActivities = Array.isArray(mappedCompany.activities);
  const hasNextJobs = Array.isArray(mappedCompany.jobs);
  const contacts = hasNextContacts
    ? getArrayOrEmpty(mappedCompany.contacts)
    : getArrayOrEmpty(previousCompany.contacts);
  const relatedLeads = hasNextRelatedLeads
    ? getArrayOrEmpty(mappedCompany.related_leads)
    : getArrayOrEmpty(previousCompany.related_leads);
  const activities = hasNextActivities
    ? getArrayOrEmpty(mappedCompany.activities)
    : getArrayOrEmpty(previousCompany.activities);
  const jobs = hasNextJobs ? getArrayOrEmpty(mappedCompany.jobs) : getArrayOrEmpty(previousCompany.jobs);

  const contactsCount = resolveMergedCount({
    nextCount: nextRow?.contactsCount ?? nextRow?.contacts_count,
    previousCount: previousCompany.contactsCount ?? previousCompany.contacts_count,
    fallbackLength: contacts.length,
    hasNextDetail: hasNextContacts,
  });
  const leadsCount = resolveMergedCount({
    nextCount: nextRow?.leadsCount ?? nextRow?.leads_count,
    previousCount: previousCompany.leadsCount ?? previousCompany.leads_count,
    fallbackLength: relatedLeads.length,
    hasNextDetail: hasNextRelatedLeads,
  });
  const jobsCount = resolveMergedCount({
    nextCount: nextRow?.jobsCount ?? nextRow?.jobs_count,
    previousCount: previousCompany.jobsCount ?? previousCompany.jobs_count,
    fallbackLength: jobs.length,
    hasNextDetail: hasNextJobs,
  });
  const candidatesCount = resolveMergedCount({
    nextCount: nextRow?.candidatesCount ?? nextRow?.candidates_count,
    previousCount: previousCompany.candidatesCount ?? previousCompany.candidates_count,
    fallbackLength: 0,
    hasNextDetail: false,
  });
  const activitiesCount = resolveMergedCount({
    nextCount: nextRow?.activitiesCount ?? nextRow?.activities_count,
    previousCount: previousCompany.activitiesCount ?? previousCompany.activities_count,
    fallbackLength: activities.length,
    hasNextDetail: hasNextActivities,
  });

  const mergedCompany = {
    ...previousCompany,
    ...mappedCompany,
    contacts,
    related_leads: relatedLeads,
    activities,
    jobs,
    contactsCount,
    contacts_count: contactsCount,
    leadsCount,
    leads_count: leadsCount,
    jobsCount,
    jobs_count: jobsCount,
    jobOpenings: jobsCount,
    job_openings: jobsCount,
    candidatesCount,
    candidates_count: candidatesCount,
    activitiesCount,
    activities_count: activitiesCount,
  };

  return reuseSelectedCompanyIfUnchanged(previousCompany, mergedCompany);
};

const mergeSelectedCompanyDetailState = (previousCompany, nextCompany) => {
  if (!nextCompany) return previousCompany || null;
  if (!previousCompany || String(previousCompany?.id || "") !== String(nextCompany?.id || "")) {
    return nextCompany;
  }

  const getArrayOrEmpty = (value) => (Array.isArray(value) ? value : []);
  const hasNextContacts = Array.isArray(nextCompany.contacts);
  const hasNextRelatedLeads = Array.isArray(nextCompany.related_leads);
  const hasNextActivities = Array.isArray(nextCompany.activities);
  const hasNextJobs = Array.isArray(nextCompany.jobs);
  const contacts = hasNextContacts
    ? getArrayOrEmpty(nextCompany.contacts)
    : getArrayOrEmpty(previousCompany.contacts);
  const relatedLeads = hasNextRelatedLeads
    ? getArrayOrEmpty(nextCompany.related_leads)
    : getArrayOrEmpty(previousCompany.related_leads);
  const activities = hasNextActivities
    ? getArrayOrEmpty(nextCompany.activities)
    : getArrayOrEmpty(previousCompany.activities);
  const jobs = hasNextJobs ? getArrayOrEmpty(nextCompany.jobs) : getArrayOrEmpty(previousCompany.jobs);

  const contactsCount = resolveMergedCount({
    nextCount: nextCompany.contactsCount ?? nextCompany.contacts_count,
    previousCount: previousCompany.contactsCount ?? previousCompany.contacts_count,
    fallbackLength: contacts.length,
    hasNextDetail: hasNextContacts,
  });
  const leadsCount = resolveMergedCount({
    nextCount: nextCompany.leadsCount ?? nextCompany.leads_count,
    previousCount: previousCompany.leadsCount ?? previousCompany.leads_count,
    fallbackLength: relatedLeads.length,
    hasNextDetail: hasNextRelatedLeads,
  });
  const jobsCount = resolveMergedCount({
    nextCount: nextCompany.jobsCount ?? nextCompany.jobs_count,
    previousCount: previousCompany.jobsCount ?? previousCompany.jobs_count,
    fallbackLength: jobs.length,
    hasNextDetail: hasNextJobs,
  });
  const candidatesCount = resolveMergedCount({
    nextCount: nextCompany.candidatesCount ?? nextCompany.candidates_count,
    previousCount: previousCompany.candidatesCount ?? previousCompany.candidates_count,
    fallbackLength: getArrayOrEmpty(previousCompany.candidates).length,
    hasNextDetail: Array.isArray(nextCompany.candidates),
  });
  const activitiesCount = resolveMergedCount({
    nextCount: nextCompany.activitiesCount ?? nextCompany.activities_count,
    previousCount: previousCompany.activitiesCount ?? previousCompany.activities_count,
    fallbackLength: activities.length,
    hasNextDetail: hasNextActivities,
  });

  const mergedCompany = {
    ...previousCompany,
    ...nextCompany,
    contacts,
    related_leads: relatedLeads,
    activities,
    jobs,
    contactsCount,
    contacts_count: contactsCount,
    leadsCount,
    leads_count: leadsCount,
    jobsCount,
    jobs_count: jobsCount,
    jobOpenings: jobsCount,
    job_openings: jobsCount,
    candidatesCount,
    candidates_count: candidatesCount,
    activitiesCount,
    activities_count: activitiesCount,
  };

  return reuseSelectedCompanyIfUnchanged(previousCompany, mergedCompany);
};

/* ------------------------- FILTER DRAWER (ReusableRightDrawer) ------------------------- */
const CompanyFilterDrawer = ({ open, onClose, rows, value, onApply, onClear, onAdvancedSearch, endpoint = "/api/companies" }) => {
  const PURPLE = "#9C4FFF";
  const LABEL_BLUE = "#3451F4";

  const uniq = (arr) =>
    Array.from(new Set([].concat(arr || []).filter(Boolean).map((x) => String(x).trim()).filter(Boolean)));

  const [draft, setDraft] = useState(value || DEFAULT_FILTERS);

  useEffect(() => {
    if (open) setDraft(value || DEFAULT_FILTERS);
  }, [open, value]);

  const directory = usePageFilterOptions(open, endpoint, draft, setDraft);
  const options = directory.options;

  const sectionLabelSx = {
    fontSize: 14,
    fontWeight: 700,
    color: LABEL_BLUE,
    mb: 0.9,
    fontFamily: "Be Vietnam Pro",
  };

  const inputSx = popupFilterInputSx(PURPLE);

  const dividerSx = { my: 1.6, borderColor: "#eef2f7" };

  const setField = (key, v) => setDraft((p) => ({ ...p, [key]: v }));

  const headerLeft = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "999px",
          border: `2px solid ${PURPLE}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fff",
        }}
      >
        <Filter size="16" color={PURPLE} variant="Linear" />
      </Box>

      <Typography data-popup-section sx={{ fontSize: 14, fontWeight: 800, color: "#3451F4", fontFamily: "Be Vietnam Pro" }}>
        Filter by
      </Typography>
    </Box>
  );

  const footer = (
    <Box sx={{ position: "sticky", bottom: 0, zIndex: 10, backgroundColor: "#fff", borderTop: "1px solid #eef2f7", py: 1.25 }}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Button
          variant="contained"
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 800,
            fontSize: 12,
            fontFamily: "Be Vietnam Pro",
            backgroundColor: PURPLE,
            boxShadow: "none",
            "&:hover": { backgroundColor: PURPLE, boxShadow: "none", opacity: 0.92 },
          }}
          onClick={() => onAdvancedSearch?.(draft)}
        >
          Advance Search
        </Button>

        <Box sx={{ flex: 1 }} />

        <Button
          variant="outlined"
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 800,
            fontSize: 12,
            fontFamily: "Be Vietnam Pro",
            borderColor: PURPLE,
            color: PURPLE,
            "&:hover": { borderColor: PURPLE, color: PURPLE, backgroundColor: "transparent", opacity: 0.92 },
          }}
          onClick={() => {
            setDraft(DEFAULT_FILTERS);
            onClear?.();
          }}
        >
          Clear Filters
        </Button>

        <Button
          variant="contained"
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 800,
            fontSize: 12,
            fontFamily: "Be Vietnam Pro",
            backgroundColor: PURPLE,
            boxShadow: "none",
            "&:hover": { backgroundColor: PURPLE, boxShadow: "none", opacity: 0.92 },
          }}
          onClick={() => onApply?.(draft, { mode: "filters" })}
        >
          Apply Filters
        </Button>
      </Stack>
    </Box>
  );

  return (
    <ReusableRightDrawer
      open={open}
      onClose={onClose}
      id="company-filter-drawer"
      width={546}
      headerLeft={headerLeft}
      closeVariant="text"
      closeColor={PURPLE}
      paperSx={{ borderRadius: "14px", overflow: "hidden" }}
      footer={footer}
    >
      <Box sx={{ pb: 8 }}>
        {directory.loading && <Typography role="status">Loading all filter values...</Typography>}
        {directory.error && <Alert severity="error">{directory.error}</Alert>}
        <Typography sx={sectionLabelSx}>AI Agent:</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Type your request"
          value={draft.ai || ""}
          onChange={(e) => setField("ai", e.target.value)}
          sx={inputSx}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Company Name:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={options.companyNames}
          {...directory.fieldProps("companyNames")}
          value={draft.companyNames || []}
          onChange={(next) => setField("companyNames", uniq(next))}
          placeholder="Type company name"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Skills:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={options.skills}
          {...directory.fieldProps("skills")}
          value={draft.techKeywords || []}
          onChange={(next) => setField("techKeywords", uniq(next))}
          placeholder="Search existing skills"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Locations:</Typography>
        <LocationMultiSelectField
          freeSolo={false}
          searchExternal={false}
          options={options.locations}
          {...directory.fieldProps("locations")}
          value={draft.locations || []}
          onChange={(next) => setField("locations", normalizeLocationFilterValues(next))}
          placeholder="Select location"
          inputSx={inputSx}
          autocompleteProps={{ ...directory.fieldProps("locations").autocompleteProps, groupBy: groupLocationOption }}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Owner:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={options.owners}
          {...directory.fieldProps("owners")}
          value={draft.owners || []}
          onChange={(next) => setField("owners", uniq(next))}
          placeholder="Select the owner"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Status:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={options.statuses}
          {...directory.fieldProps("statuses")}
          value={draft.statuses || []}
          onChange={(next) => setField("statuses", uniq(next))}
          placeholder="Select one or more Status"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Activity:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={options.activities}
          {...directory.fieldProps("activities")}
          value={draft.activities || []}
          onChange={(next) => setField("activities", uniq(next))}
          placeholder="Select one or more activities"
          inputSx={inputSx}
        />

        <Divider sx={dividerSx} />

        <Typography sx={sectionLabelSx}>Source:</Typography>
        <PopupMultiSelectField
          freeSolo={false}
          options={options.sources}
          {...directory.fieldProps("sources")}
          value={draft.sources || []}
          onChange={(next) => setField("sources", uniq(next))}
          placeholder="Select one or more Sources"
          inputSx={inputSx}
        />
      </Box>
    </ReusableRightDrawer>
  );
};

/* ------------------------------ MAIN ------------------------------ */
function ClientsContent({ user }) {
  const API_BASE = process.env.REACT_APP_API_BASE;

  const location = useLocation();
  const navigate = useNavigate();
  const sidebarResetToken = getSidebarResetToken(location, "/companies");
  const initialListTab = sidebarResetToken ? SIDEBAR_HOME_LIST_TAB : DEFAULT_LIST_TAB;

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedIdsLocal, setSelectedIdsLocal] = useState([]);
  const [isAddToSequenceOpen, setIsAddToSequenceOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [companyDrawerOpen, setCompanyDrawerOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCompanyEntityType, setSelectedCompanyEntityType] = useState(
    initialListTab === LIST_TABS.companies ? "company" : "lead"
  );
  const [selectedCompanyParent, setSelectedCompanyParent] = useState(null);
  const [selectedCompanyParentTab, setSelectedCompanyParentTab] = useState("details");
  const [listTab, setListTab] = useState(initialListTab);
  const [activeStatId, setActiveStatId] = useState("total");
  const overviewMetric = useMemo(
    () => new URLSearchParams(location.search).get("overviewMetric") || "",
    [location.search]
  );

  useEffect(() => {
    const statByMetric = {
      active_clients: "active",
    };
    const statId = statByMetric[overviewMetric];
    if (statId) {
      setListTab(LIST_TABS.clients);
      setActiveStatId(statId);
    }
  }, [overviewMetric]);
  const [returnListTab, setReturnListTab] = useState(initialListTab);

  const [viewMode, setViewMode] = useState("list"); // list | leadTabsLayout
  const [profileTab, setProfileTab] = useState("details");

  const [leadRows, setLeadRows] = useState([]);
  const [companyRows, setCompanyRows] = useState([]);
  const [companyRowsTotal, setCompanyRowsTotal] = useState(0);
  const [clientRowsTotal, setClientRowsTotal] = useState(0);
  const [companyTablePage, setCompanyTablePage] = useState(0);
  const [companyTableRowsPerPage, setCompanyTableRowsPerPage] = useState(10);
  const [contactRows, setContactRows] = useState([]);
  const [contactRowsTotal, setContactRowsTotal] = useState(null);
  const [contactTablePage, setContactTablePage] = useState(0);
  const [contactTableRowsPerPage, setContactTableRowsPerPage] = useState(10);
  const [salesSequenceRows, setSalesSequenceRows] = useState([]);
  const [teamDirectoryRows, setTeamDirectoryRows] = useState([]);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [contactFilters, setContactFilters] = useState({});

  // Advanced Search state (Clients page)
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advancedApplied, setAdvancedApplied] = useState(null);
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useCsvImport();
  const [addJobOpen, setAddJobOpen] = useState(false);
  const [companyLeadDialogOpen, setCompanyLeadDialogOpen] = useState(false);
  const [companyLeadDialogMode, setCompanyLeadDialogMode] = useState("lead");
  const [companyLeadDialogValue, setCompanyLeadDialogValue] = useState(null);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [addContactSaving, setAddContactSaving] = useState(false);
  const [addContactError, setAddContactError] = useState("");
  const [addContactTarget, setAddContactTarget] = useState(null);
  const [addContactForm, setAddContactForm] = useState({
    name: "",
    role: "",
    email: "",
    phone: "",
    linkedin_url: "",
    source: "Manual",
  });
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [addNoteSaving, setAddNoteSaving] = useState(false);
  const [addNoteError, setAddNoteError] = useState("");
  const [addNoteTitle, setAddNoteTitle] = useState("Note");
  const [addNoteBody, setAddNoteBody] = useState("");
  const [addNoteTargets, setAddNoteTargets] = useState([]);
  const [editCompanyOpen, setEditCompanyOpen] = useState(false);
  const [editCompanySaving, setEditCompanySaving] = useState(false);
  const [editCompanyError, setEditCompanyError] = useState("");
  const [editCompanyTarget, setEditCompanyTarget] = useState(null);
  const [editCompanyForm, setEditCompanyForm] = useState({
    name: "",
    website: "",
    location: "",
    industry: "",
    status: "New",
  });
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
  const [rowMenuCompany, setRowMenuCompany] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [isAgentDialogOpen, setIsAgentDialogOpen] = useState(false);
  const [agentDialogInitialPrompt, setAgentDialogInitialPrompt] = useState("");
  const [agentDialogSubmitOnOpen, setAgentDialogSubmitOnOpen] = useState(false);

  const [statusSaving, setStatusSaving] = useState(false);

  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });
  const [statusToast, setStatusToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const [companyRouteError, setCompanyRouteError] = useState("");
  const [companyRouteRetry, setCompanyRouteRetry] = useState(0);
  // A profile can remain legitimately incomplete after its background AI pass.
  // Record that its detail request has completed so the route effect does not
  // repeatedly replace the saved detail object with the compact list row.
  const hydratedCompanyRouteKeyRef = useRef("");
  const pendingCompanyTabUrlSyncRef = useRef("");
  const isAdminUser = ["admin", "administrator", "system_user"].includes(
    String(user?.role || "").trim().toLowerCase()
  );
  const [companyDialogInitialValues, setCompanyDialogInitialValues] = useState({
    status: ACTIVE_CLIENT_COMPANY_STATUS,
  });

  useEffect(() => {
    if (!sidebarResetToken) return;

    setShowAdvancedSearch(false);
    setCompanyDrawerOpen(false);
    setSelectedCompany(null);
    setSelectedCompanyEntityType("company");
    setSelectedCompanyParent(null);
    setSelectedCompanyParentTab("details");
    setViewMode("list");
    setProfileTab("details");
    setListTab(SIDEBAR_HOME_LIST_TAB);
    setReturnListTab(SIDEBAR_HOME_LIST_TAB);
    try {
      sessionStorage.removeItem(NAV_CACHE_KEY);
    } catch {
      // ignore session storage failures
    }
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.search, navigate, sidebarResetToken]);

  const normalizeId = (v) => {
    if (v === null || v === undefined) return null;
    const s = String(v);
    const n = Number(s);
    return Number.isFinite(n) && String(n) === s ? n : s;
  };

  const getToken = useCallback(() => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      localStorage.getItem("jwt") ||
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("accessToken") ||
      sessionStorage.getItem("jwt") ||
      ""
    );
  }, []);

  const authHeaders = useCallback(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  const buildDetailNavPayload = useCallback(
    (companyOverride = null, tabOverride = profileTab) => {
      const companyToPersist = companyOverride || selectedCompany;
      if (!companyToPersist?.id) return null;
      const normalizedOpenEntityType =
        String(companyToPersist?.entityType || selectedCompanyEntityType || "lead").toLowerCase() === "company"
          ? "company"
          : "lead";
      const normalizedParentCompany =
        normalizedOpenEntityType === "lead" && selectedCompanyParent?.id
          ? selectedCompanyParent
          : null;

      const normalizedReturnListTab =
        returnListTab === LIST_TABS.companies || returnListTab === LIST_TABS.clients
          ? returnListTab
          : normalizedOpenEntityType === "company"
            ? LIST_TABS.companies
            : LIST_TABS.clients;

      return buildOpenCompanyNavPayload({
        company: companyToPersist,
        defaultTab: normalizeTab(tabOverride),
        returnListTab: normalizedReturnListTab,
        entityType: normalizedOpenEntityType,
        parentCompany: normalizedParentCompany,
        parentTab: normalizedParentCompany
          ? normalizeTab(selectedCompanyParentTab || "leads")
          : "",
      });
    },
    [
      profileTab,
      returnListTab,
      selectedCompany,
      selectedCompanyEntityType,
      selectedCompanyParent,
      selectedCompanyParentTab,
    ]
  );

  // The route owns opening a profile. List refreshes and child updates must
  // never restart its hydration or restore an older navigation snapshot.
  const companyNavigationSnapshotRef = useRef(null);
  companyNavigationSnapshotRef.current = {
    selectedCompany, selectedCompanyEntityType, companyRows, leadRows, listTab,
  };

  useEffect(() => {
    if (sidebarResetToken) return undefined;
    const params = new URLSearchParams(location.search);
    const companyId = params.get("companyId");
    if (!companyId) {
      setViewMode("list");
      hydratedCompanyRouteKeyRef.current = "";
      return undefined;
    }

    const snapshot = companyNavigationSnapshotRef.current;
    const explicitType = String(params.get("entityType") || "").toLowerCase();
    const hasExplicitType = ["company", "lead", "contact"].includes(explicitType);
    const matchesRoute = (payload) =>
      payload?.viewMode === "leadTabsLayout" &&
      String(payload?.openCompany?.id || "") === String(companyId) &&
      (!hasExplicitType || payload.openCompany.entityType === explicitType);
    let payload = matchesRoute(location.state) ? location.state : null;
    if (!payload) {
      try {
        const cached = JSON.parse(sessionStorage.getItem(NAV_CACHE_KEY) || "null");
        if (matchesRoute(cached)) payload = cached;
      } catch { /* Storage can be unavailable. The URL still loads the record. */ }
    }
    const entityType = hasExplicitType ? explicitType :
      payload?.openCompany?.entityType ||
      (snapshot.companyRows.some(row => String(row.id) === String(companyId)) ? "company" :
        snapshot.leadRows.some(row => String(row.id) === String(companyId)) ? "lead" : "company");
    const routeKey = `${entityType}:${companyId}`;
    const isCurrentRecord =
      String(snapshot.selectedCompany?.id || "") === String(companyId) &&
      snapshot.selectedCompanyEntityType === entityType;
    const requestedTab = normalizeTab(params.get("tab") || payload?.defaultTab);
    const requestedReturnTab = params.get("returnTab") || payload?.returnListTab;
    const returnTab = [LIST_TABS.companies, LIST_TABS.clients].includes(requestedReturnTab)
      ? requestedReturnTab : entityType === "company" ? LIST_TABS.companies : LIST_TABS.clients;
    const parentCompany = entityType === "lead" && payload?.parentCompany?.id
      ? { ...payload.parentCompany, entityType: "company" } : null;

    setProfileTab(requestedTab);
    setSelectedCompanyEntityType(entityType);
    setSelectedCompanyParent(parentCompany);
    setSelectedCompanyParentTab(parentCompany ? normalizeTab(payload?.parentTab || "leads") : "details");
    setReturnListTab(returnTab);
    setListTab(returnTab);
    setViewMode("leadTabsLayout");
    setCompanyDrawerOpen(false);
    // Keep an already open profile mounted, including while changing tabs.
    // Fresh company routes wait for details once instead of flashing a table seed.
    if (!isCurrentRecord) {
      setSelectedCompany(entityType === "company" ? null : payload?.openCompany || null);
    }
    if (isCurrentRecord && hydratedCompanyRouteKeyRef.current === routeKey) return undefined;

    const controller = new AbortController();
    let active = true;
    const load = async () => {
      const types = hasExplicitType ? [entityType] :
        entityType === "company" ? ["company", "lead"] : ["lead", "company"];
      let lastError;
      for (const type of types) {
        const response = await fetch(`${API_BASE}/api/${type === "company" ? "companies" : "leads"}/${encodeURIComponent(companyId)}`, {
          headers: authHeaders(), credentials: "include", signal: controller.signal,
        });
        if (!response.ok) {
          lastError = new Error(`Unable to load ${type} profile (${response.status}).`);
          continue;
        }
        const data = await response.json();
        if (!active) return;
        const row = data?.company || data?.lead || data;
        if (!row?.id) throw new Error("The profile response did not contain a record.");
        const company = type === "company" ? mapCompanyRowToSelectedCompany(row) : mapLeadRowToSelectedCompany(row);
        hydratedCompanyRouteKeyRef.current = `${type}:${companyId}`;
        setSelectedCompany(previous => mergeSelectedCompanyDetailState(previous, { ...company, entityType: type }));
        setSelectedCompanyEntityType(type);
        return;
      }
      throw lastError || new Error("Unable to load company profile.");
    };
    load().catch(error => {
      if (!active || error?.name === "AbortError") return;
      console.error("Failed to open company profile:", error);
      // Keep cached content usable on a temporary failure, without routing away.
      if (!isCurrentRecord && payload?.openCompany) setSelectedCompany(payload.openCompany);
      setCompanyRouteError(error.message);
    });
    setCompanyRouteError("");
    return () => { active = false; controller.abort(); };
  }, [location.search, location.state, sidebarResetToken, API_BASE, authHeaders, companyRouteRetry]);

  useEffect(() => {
    if (viewMode !== "leadTabsLayout" || sidebarResetToken) return;

    const payload = buildDetailNavPayload();
    if (!payload) return;

    try {
      sessionStorage.setItem(NAV_CACHE_KEY, JSON.stringify(payload));
    } catch {
      // ignore session storage failures
    }

    const params = new URLSearchParams(location.search);
    const currentCompanyId = params.get("companyId");
    const currentTab = normalizeTab(params.get("tab"));
    const currentEntityType = String(params.get("entityType") || "").toLowerCase();
    const currentReturnTab = params.get("returnTab");
    const nextCompanyId = String(payload.openCompany.id);
    const nextTab = payload.defaultTab;
    const nextEntityType = payload.openCompany.entityType;
    const nextReturnTab = payload.returnListTab;

    // Route-driven navigation applies profileTab in a separate effect. Until it
    // catches up, an explicit incoming URL tab must not be replaced by stale
    // local state. This ref is set only for a user-initiated tab selection.
    if (
      currentTab !== nextTab &&
      pendingCompanyTabUrlSyncRef.current !== nextTab
    ) {
      return;
    }

    // Opening/closing detail views is handled explicitly by click handlers.
    // Only sync the URL once the selected entity and the current route already
    // point at the same detail record; otherwise we can rewrite newer navigation
    // with stale state from the previous detail view.
    if (!currentCompanyId || String(currentCompanyId) !== nextCompanyId) {
      return;
    }

    if (currentEntityType && currentEntityType !== nextEntityType) {
      return;
    }

    if (
      String(currentCompanyId || "") === nextCompanyId &&
      currentTab === nextTab &&
      currentEntityType === nextEntityType &&
      currentReturnTab === nextReturnTab
    ) {
      pendingCompanyTabUrlSyncRef.current = "";
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: `?companyId=${encodeURIComponent(nextCompanyId)}&tab=${encodeURIComponent(nextTab)}&entityType=${encodeURIComponent(nextEntityType)}&returnTab=${encodeURIComponent(nextReturnTab)}`,
      },
      { replace: true, state: payload }
    );
    pendingCompanyTabUrlSyncRef.current = "";
  }, [buildDetailNavPayload, location.pathname, location.search, location.state, navigate, sidebarResetToken, viewMode]);

  const closeSelectedCompanyView = useCallback(() => {
    setListTab(returnListTab);
    setViewMode("list");
    setSelectedCompany(null);
    setSelectedCompanyParent(null);
    setSelectedCompanyParentTab("details");
    setCompanyDrawerOpen(false);
    setIsFilterOpen(false);
    setShowAdvancedSearch(false);
    setIsAddToSequenceOpen(false);
    setAddClientOpen(false);
    setProfileTab("details");

    try {
      sessionStorage.removeItem(NAV_CACHE_KEY);
    } catch {
      // ignore session storage failures
    }

    navigate(
      {
        pathname: location.pathname,
        search: "",
      },
      {
        replace: true,
        state: createSidebarResetState(location.pathname),
      }
    );
  }, [location.pathname, navigate, returnListTab]);

  const loadLeadRows = useCallback(async () => {
    const leadsRes = await fetch(`${API_BASE}/api/leads?page=1&limit=1000&sort=id&order=desc`, {
      headers: { ...authHeaders() },
      credentials: "include",
    });

    if (!leadsRes.ok) {
      const txt = await leadsRes.text().catch(() => "");
      throw new Error(txt || `Failed to fetch leads (${leadsRes.status})`);
    }

    const leadsData = await leadsRes.json().catch(() => ({}));
    return (Array.isArray(leadsData) ? leadsData : leadsData.rows || []).map(mapLeadRowToListRow);
  }, [API_BASE, authHeaders]);

  const loadContactRows = useCallback(async ({ page = 1, limit = 10, search = "" } = {}) => {
    const query = buildContactListQuery({ page, limit, search, ...contactFilters });

    const res = await fetch(`${API_BASE}/api/hiring-managers?${query.toString()}`, {
      headers: { ...authHeaders() },
      credentials: "include",
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(txt || `Failed to fetch contacts (${res.status})`);
    }

    const data = await res.json().catch(() => ({}));
    const rows = (Array.isArray(data) ? data : data.rows || []).map((row) => ({
      id: row.id,
      entityType: "contact",
      name: row.name || "",
      status: row.status || "New",
      role: normalizeContactRoleLabel(row.role || row.title, {
        contactKind: row.contact_kind,
        source: row.source,
        fallback: "Hiring Manager",
      }),
      // Source in this list means the responsible person, not the acquisition
      // method (for example, never render "AI Team Scan" as an owner).
      source: row.contact_owner || row.owner || row.owner_name || row.team_member || "-",
      owner: row.contact_owner || row.owner || row.owner_name || row.team_member || "",
      email: row.email || "",
      phone: row.phone || "",
      linkedin_url: row.linkedin_url || "",
      entity_type: row.entity_type || "",
      entity_id: row.entity_id,
      entity_name: row.entity_name || "",
      entity_logo: row.entity_logo || "",
      entity_website: row.entity_website || "",
      contact_kind: row.contact_kind || "",
      created_at: row.created_at,
      updated_at: row.updated_at,
      raw: row,
    }));
    const total = Number(data?.total);
    return {
      rows,
      total: Number.isFinite(total) && total >= 0 ? total : rows.length,
    };
  }, [API_BASE, authHeaders, contactFilters]);

  const loadContactRowsTotal = useCallback(async () => {
    const response = await fetch(`${API_BASE}/api/hiring-managers?summary=count`, {
      headers: { ...authHeaders() },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch contacts count (${response.status})`);
    }

    const data = await response.json().catch(() => ({}));
    const count = Number(data?.count);
    return Number.isFinite(count) && count >= 0 ? count : 0;
  }, [API_BASE, authHeaders]);

  const loadCompanyRows = useCallback(async () => {
    const scope = listTab === LIST_TABS.clients ? "client" : "company";
    const shouldLoadAllRows =
      Boolean(search.trim()) ||
      (activeStatId && activeStatId !== "total") ||
      hasBasicCompanyFilters(filters) ||
      hasAdvancedCompanyFilters(advancedApplied);
    if (shouldLoadAllRows) {
      const raw = await loadFilterRecords(`/api/companies?scope=${scope}&include_contact_hydration=0`);
      if (scope === "client") setClientRowsTotal(raw.length);
      else setCompanyRowsTotal(raw.length);
      return raw.map(mapCompanyRowToListRow);
    }
    const companiesRes = await fetch(
      `${API_BASE}/api/companies?page=${shouldLoadAllRows ? 1 : companyTablePage + 1}&limit=${shouldLoadAllRows ? 1000 : companyTableRowsPerPage}&scope=${scope}&include_contact_hydration=0`,
      {
      headers: { ...authHeaders() },
      credentials: "include",
      }
    );

    if (!companiesRes.ok) {
      const txt = await companiesRes.text().catch(() => "");
      throw new Error(txt || `Failed to fetch companies (${companiesRes.status})`);
    }

    const companiesData = await companiesRes.json().catch(() => ({}));
    const raw = Array.isArray(companiesData) ? companiesData : companiesData.rows || [];
    const total = Number(companiesData?.total);
    const resolvedTotal = Number.isFinite(total) ? Math.max(0, total) : raw.length;
    if (scope === "client") setClientRowsTotal(resolvedTotal);
    else setCompanyRowsTotal(resolvedTotal);
    return raw.map(mapCompanyRowToListRow);
  }, [API_BASE, activeStatId, advancedApplied, authHeaders, companyTablePage, companyTableRowsPerPage, filters, listTab, search]);

  const loadSalesSequenceRows = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/sequences`, {
      headers: { ...authHeaders() },
      credentials: "include",
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(txt || `Failed to fetch sequences (${res.status})`);
    }

    const data = await res.json().catch(() => ({}));
    return Array.isArray(data) ? data : data.rows || [];
  }, [API_BASE, authHeaders]);

  useEffect(() => {
    let active = true;
    const loadTabTotals = async () => {
      const [clientResponse, companyResponse] = await Promise.all([
        fetch(`${API_BASE}/api/companies?page=1&limit=1&scope=client&include_contact_hydration=0`, { headers: { ...authHeaders() }, credentials: "include" }),
        fetch(`${API_BASE}/api/companies?page=1&limit=1&scope=company&include_contact_hydration=0`, { headers: { ...authHeaders() }, credentials: "include" }),
      ]);
      const [clientData, companyData] = await Promise.all([
        clientResponse.ok ? clientResponse.json() : Promise.resolve({}),
        companyResponse.ok ? companyResponse.json() : Promise.resolve({}),
      ]);
      if (!active) return;
      const clientTotal = Number(clientData?.total);
      const companyTotal = Number(companyData?.total);
      if (Number.isFinite(clientTotal)) setClientRowsTotal(Math.max(0, clientTotal));
      if (Number.isFinite(companyTotal)) setCompanyRowsTotal(Math.max(0, companyTotal));
    };
    loadTabTotals().catch((error) => console.warn("Failed to fetch company tab totals:", error));
    return () => { active = false; };
  }, [API_BASE, authHeaders, reloadKey]);

  const syncSelectedCompanyFromCompanyRows = useCallback((nextCompanyRows = []) => {
    setSelectedCompany((prev) => {
      if (!prev || String(prev?.entityType || "").toLowerCase() !== "company") return prev;

      const match = nextCompanyRows.find((row) => String(row?.id) === String(prev?.id));
      return match ? mergeSelectedCompanyFromListRow(prev, match.raw || match) : prev;
    });
  }, []);

  useEffect(() => subscribeCompanyRelationshipsChanged(() => {
    setReloadKey((value) => value + 1);
  }), []);

  const refreshCompanyRows = useCallback(async () => {
    const nextCompanyRows = await loadCompanyRows();
    setCompanyRows(nextCompanyRows);
    syncSelectedCompanyFromCompanyRows(nextCompanyRows);
    return nextCompanyRows;
  }, [loadCompanyRows, syncSelectedCompanyFromCompanyRows]);

  useEffect(() => {
    const refreshApprovedReviewRecords = async () => {
      try {
        const [nextCompanyRows, nextLeadRows, nextContactResult] = await Promise.all([
          loadCompanyRows(),
          loadLeadRows(),
          loadContactRows(),
        ]);
        setCompanyRows(nextCompanyRows);
        syncSelectedCompanyFromCompanyRows(nextCompanyRows);
        setLeadRows(nextLeadRows);
        setContactRows(nextContactResult.rows);
        setContactRowsTotal(nextContactResult.total);
      } catch (error) {
        console.warn("Failed to refresh approved company review records:", error);
      }
    };

    window.addEventListener("rpo:company-review-records-updated", refreshApprovedReviewRecords);
    return () => {
      window.removeEventListener("rpo:company-review-records-updated", refreshApprovedReviewRecords);
    };
  }, [loadCompanyRows, loadContactRows, loadLeadRows, syncSelectedCompanyFromCompanyRows]);

  // Table rows are also a source of truth for active company AI work. Register
  // them here so the global notice is visible before a user opens a profile.
  useEffect(() => {
    companyRows.forEach((row) => {
      syncCompanyBackgroundWork(row?.raw || row);
    });
  }, [companyRows]);

  useEffect(() => {
    const hasBackgroundWork = companyRows.some(
      (row) => row?.contact_repair_pending || row?.company_profile_enrichment_pending
    );
    if (!hasBackgroundWork) return undefined;

    const timeoutId = window.setTimeout(() => {
      refreshCompanyRows().catch((error) => {
        console.warn("Failed to refresh company background-task status:", error);
      });
    }, 1500);

    return () => window.clearTimeout(timeoutId);
  }, [companyRows, refreshCompanyRows]);

  useEffect(() => {
    let active = true;

    const fetchLists = async () => {
      // A Contacts pagination change must not wait for a full Companies list.
      // That request can be hundreds of records and was previously made before
      // every 10-row Contacts page request in production.
      if (listTab !== LIST_TABS.contacts) {
        try {
          const nextCompanyRows = await loadCompanyRows();
          if (!active) return;
          setCompanyRows(nextCompanyRows);
          syncSelectedCompanyFromCompanyRows(nextCompanyRows);
        } catch (e) {
          console.error("Failed to fetch client/company lists:", e);
          if (!active) return;
          setCompanyRows([]);
        }
        return;
      }

      try {
        const nextContactResult = await loadContactRows({
          page: contactTablePage + 1,
          limit: contactTableRowsPerPage,
          search,
        });
        if (!active) return;
        setContactRows(nextContactResult.rows);
        setContactRowsTotal(nextContactResult.total);
      } catch (e) {
        console.error("Failed to fetch contact list:", e);
        if (active) setContactRows([]);
      }
    };

    fetchLists();
    return () => {
      active = false;
    };
  }, [
    loadCompanyRows,
    loadContactRows,
    contactTablePage,
    contactTableRowsPerPage,
    listTab,
    reloadKey,
    viewMode,
    search,
    syncSelectedCompanyFromCompanyRows,
  ]);

  useEffect(() => {
    let active = true;

    loadContactRowsTotal()
      .then((count) => {
        if (active) setContactRowsTotal(count);
      })
      .catch((error) => {
        // The badge can safely fall back to loaded rows if the count request
        // is unavailable; do not make the Companies table fail to render.
        console.warn("Failed to fetch contacts count:", error);
      });

    return () => {
      active = false;
    };
  }, [loadContactRowsTotal, reloadKey]);

  useEffect(() => {
    let active = true;

    const loadSupplementalStats = async () => {
      const [nextSalesSequenceRows, recruiterUsers, teamMembers] = await Promise.all([
        loadSalesSequenceRows().catch((error) => {
          console.warn("Failed to fetch sales sequences for company stats:", error);
          return [];
        }),
        fetchRecruiterUsers({ apiBase: API_BASE }).catch((error) => {
          console.warn("Failed to fetch recruiter users for company stats:", error);
          return [];
        }),
        fetchTeamMembers({ apiBase: API_BASE }).catch((error) => {
          console.warn("Failed to fetch team members for company stats:", error);
          return [];
        }),
      ]);

      if (!active) return;

      setSalesSequenceRows(nextSalesSequenceRows);

      const seenNames = new Set();
      const mergedTeamDirectory = [...recruiterUsers, ...teamMembers].filter((record) => {
        const normalizedName = cleanFilterValue(record?.name).toLowerCase();
        if (!normalizedName || seenNames.has(normalizedName)) return false;
        seenNames.add(normalizedName);
        return true;
      });

      setTeamDirectoryRows(mergedTeamDirectory);
    };

    loadSupplementalStats();
    return () => {
      active = false;
    };
  }, [API_BASE, loadSalesSequenceRows, reloadKey]);

  const clientRows = useMemo(
    () => getCompanyTabRows(companyRows, LIST_TABS.clients),
    [companyRows]
  );

  const companiesTabRows = useMemo(
    () => getCompanyTabRows(companyRows, LIST_TABS.companies),
    [companyRows]
  );
  const salesSequenceKeySet = useMemo(() => {
    const next = new Set();

    salesSequenceRows.forEach((row) => {
      if (!SALES_SEQUENCE_ENTITY_TYPES.has(normalizeSequenceEntityType(row?.entity_type))) return;

      buildSalesSequenceEntityKeys(
        row?.entity_type,
        row?.entity_id ?? row?.entityId ?? row?.id
      ).forEach((key) => next.add(key));
    });

    return next;
  }, [salesSequenceRows]);
  const teamDirectoryLookup = useMemo(() => {
    const nextLookup = new Map();

    teamDirectoryRows.forEach((member) => {
      const normalizedName = normalizeInlineTextLower(member?.name);
      if (!normalizedName || nextLookup.has(normalizedName)) return;
      nextLookup.set(normalizedName, member);
    });

    return nextLookup;
  }, [teamDirectoryRows]);

  const enrichedContactRows = useMemo(() => {
    const leadMap = new Map(leadRows.map((r) => [String(r.id), r]));
    const companyMap = new Map(companyRows.map((r) => [String(r.id), r]));
    return contactRows.map((c) => {
      const entityType = normalizeHiringManagerEntityType(c.entity_type);
      const entityId = String(c.entity_id ?? "");

      let entity = null;
      if (entityType === "lead") entity = leadMap.get(entityId);
      else entity = companyMap.get(entityId);
      const entityOwner = entity?.owner_name || entity?.owner || entity?.team_member || "";
      if (entityOwner && areSamePersonNames(c.name, entityOwner)) {
        return null;
      }
      return {
        ...c,
        entity_type: entityType || c.entity_type || "",
        entity_name: c.entity_name || entity?.name || "",
        entity_logo: c.entity_logo || entity?.logo || "",
        entity_website: c.entity_website || entity?.website || "",
      };
    }).filter(Boolean);
  }, [contactRows, leadRows, companyRows]);

  const showingCompanies = listTab === LIST_TABS.companies;
  const showingContacts = listTab === LIST_TABS.contacts;
  const contactsUseServerPagination = showingContacts && activeStatId === "total";
  const hasCompanyFiltersApplied = useMemo(() => hasBasicCompanyFilters(filters), [filters]);
  const hasCompanyAdvancedFiltersApplied = useMemo(
    () => hasAdvancedCompanyFilters(advancedApplied),
    [advancedApplied]
  );
  const shouldExpandCompanyRows = !showingContacts && (
    Boolean(search.trim()) ||
    (activeStatId && activeStatId !== "total") ||
    hasCompanyFiltersApplied ||
    hasCompanyAdvancedFiltersApplied
  );

  const companyAdvancedSearchOptions = useMemo(
    () => ({
      locations: buildLocationOptions(companyRows),
      statuses: COMPANY_STATUS_OPTIONS,
    }),
    [companyRows]
  );
  const companyDetailFieldOptions = useMemo(
    () => buildDetailFieldOptions(companyRows),
    [companyRows]
  );
  const leadDetailFieldOptions = useMemo(
    () => buildDetailFieldOptions(leadRows),
    [leadRows]
  );
  const activeRows = useMemo(
    () => {
      if (listTab === LIST_TABS.contacts) return enrichedContactRows;
      if (shouldExpandCompanyRows) return companyRows;
      if (listTab === LIST_TABS.companies) return companiesTabRows;
      return clientRows;
    },
    [listTab, companyRows, companiesTabRows, clientRows, enrichedContactRows, shouldExpandCompanyRows]
  );

  const openAddCompanyDialog = useCallback(() => {
    setCompanyDialogInitialValues({ status: "New" });
    setAddClientOpen(true);
  }, []);

  const openCompany = useCallback((row) => {
    const nextEntityType = row?.entityType || "lead";
    setSelectedCompanyEntityType(nextEntityType);
    setSelectedCompany(
      nextEntityType === "company"
        ? mapCompanyRowToSelectedCompany(row.raw || row)
        : mapLeadRowToSelectedCompany(row.raw || row)
    );
    setCompanyDrawerOpen(true);
  }, []);
  const handleOpenCompanyContacts = useCallback((row) => {
    const companyName = cleanFilterValue(row?.name || row?.company_name || row?.raw?.company_name);
    setListTab(LIST_TABS.contacts);
    setSearch(companyName);
  }, []);

  useEffect(() => {
    if (viewMode !== "list") return;

    setSelectedIds([]);
    setSelectedIdsLocal([]);
    setCompanyDrawerOpen(false);
    setSelectedCompany(null);
    setSelectedCompanyParent(null);
    setSelectedCompanyParentTab("details");
    setSelectedCompanyEntityType(listTab === LIST_TABS.companies ? "company" : listTab === LIST_TABS.contacts ? "contact" : "lead");
  }, [listTab, viewMode]);

  const handleSelectedCompanyChange = useCallback((nextCompany) => {
    if (!nextCompany?.id) return;

    const nextEntityType =
      String(nextCompany?.entityType || selectedCompanyEntityType || "lead").toLowerCase() === "company"
        ? "company"
        : "lead";
    const isViewingLeadDetail =
      String(selectedCompanyEntityType || "lead").toLowerCase() === "lead";
    const isParentCompanyRefreshWhileViewingLead =
      isViewingLeadDetail &&
      nextEntityType === "company" &&
      selectedCompanyParent?.id &&
      String(nextCompany.id) === String(selectedCompanyParent.id);

    if (isViewingLeadDetail && nextEntityType === "company") {
      if (isParentCompanyRefreshWhileViewingLead) {
        const mergedParentCompany = mergeSelectedCompanyDetailState(selectedCompanyParent, {
          ...nextCompany,
          entityType: "company",
        });
        setSelectedCompanyParent(mergedParentCompany);
        setCompanyRows((prev) =>
          prev.map((row) =>
            String(row.id) === String(mergedParentCompany.id)
              ? {
                  ...row,
                  ...mergedParentCompany,
                  entityType: "company",
                  name: mergedParentCompany.name || row.name,
                  status: mergedParentCompany.status || row.status,
                  website:
                    mergedParentCompany.website ||
                    mergedParentCompany.website_url ||
                    row.website,
                  owner:
                    mergedParentCompany.owner ||
                    mergedParentCompany.owner_name ||
                    row.owner,
                  logo: mergedParentCompany.logo || row.logo,
                  location: mergedParentCompany.location || row.location,
                  source: mergedParentCompany.source || row.source,
                  ...buildListLastActivityFields(mergedParentCompany, {
                    companyEntity: true,
                  }),
                }
              : row
          )
        );
      }
      return;
    }

    const mergedNextCompany = mergeSelectedCompanyDetailState(selectedCompany, {
      ...nextCompany,
      entityType: nextEntityType,
    });

    setSelectedCompany(mergedNextCompany);
    if (nextEntityType === "company") {
      setCompanyRows((prev) =>
        prev.map((row) =>
          String(row.id) === String(mergedNextCompany.id)
            ? {
                ...row,
                ...mergedNextCompany,
                entityType: nextEntityType,
                name: mergedNextCompany.name || row.name,
                status: mergedNextCompany.status || row.status,
                website: mergedNextCompany.website || mergedNextCompany.website_url || row.website,
                owner: mergedNextCompany.owner || mergedNextCompany.owner_name || row.owner,
                logo: mergedNextCompany.logo || row.logo,
                location: mergedNextCompany.location || row.location,
                source: mergedNextCompany.source || row.source,
                ...buildListLastActivityFields(mergedNextCompany, {
                  companyEntity: true,
                }),
              }
            : row
        )
      );
      return;
    }

    setLeadRows((prev) =>
      prev.map((row) =>
        String(row.id) === String(mergedNextCompany.id)
          ? {
              ...row,
              ...mergedNextCompany,
              entityType: nextEntityType,
              name: mergedNextCompany.name || mergedNextCompany.company_name || row.name,
              status: mergedNextCompany.status || row.status,
              website: mergedNextCompany.website || mergedNextCompany.website_url || row.website,
              owner: mergedNextCompany.owner || mergedNextCompany.owner_name || row.owner,
              logo: mergedNextCompany.logo || mergedNextCompany.company_logo || row.logo,
              location: mergedNextCompany.location || row.location,
              source: mergedNextCompany.source || row.source,
              jobOpenings:
                Number(
                  mergedNextCompany.jobsCount ??
                    mergedNextCompany.jobs_count ??
                    0
                ) || 0,
              ...buildListLastActivityFields(mergedNextCompany),
            }
          : row
      )
    );
  }, [selectedCompany, selectedCompanyEntityType, selectedCompanyParent]);

  const handleSelectedCompanyTabChange = useCallback(
    (nextTab) => {
      const nextProfileTab = normalizeTab(nextTab);
      const params = new URLSearchParams(location.search);
      const routeCompanyId = params.get("companyId");
      const routeTab = params.get("tab");

      // During URL-driven navigation the tab layout can briefly emit its previous
      // local tab before React applies the new initialTab prop. The explicit URL
      // is newer than that callback, so keep it authoritative and avoid changing
      // jobs back to details (or vice versa).
      if (
        routeTab &&
        routeCompanyId &&
        selectedCompany?.id != null &&
        String(routeCompanyId) === String(selectedCompany.id) &&
        normalizeTab(routeTab) !== normalizeTab(profileTab) &&
        nextProfileTab === normalizeTab(profileTab)
      ) {
        return;
      }

      if (nextProfileTab !== normalizeTab(profileTab)) {
        pendingCompanyTabUrlSyncRef.current = nextProfileTab;
      }
      setProfileTab(nextProfileTab);
    },
    [location.search, profileTab, selectedCompany?.id]
  );

  const handleShowMoreCompany = useCallback(
    (tab = "details", nextCompany = null, options = {}) => {
      const companyToOpen = nextCompany || selectedCompany;
      if (!companyToOpen?.id) return;

      const safeTab = normalizeTab(tab);
      const nextEntityType =
        String(companyToOpen?.entityType || selectedCompanyEntityType || "lead").toLowerCase() === "company"
          ? "company"
          : "lead";
      const normalizedParentCompany =
        nextEntityType === "lead" && options?.parentCompany?.id
          ? buildOpenCompanyNavEntity(mapCompanyRowToSelectedCompany(options.parentCompany), {
              entityType: "company",
            })
          : null;
      const payload = buildOpenCompanyNavPayload({
        company: companyToOpen,
        defaultTab: safeTab,
        returnListTab: listTab,
        entityType: nextEntityType,
        parentCompany: normalizedParentCompany,
        parentTab: normalizedParentCompany
          ? normalizeTab(options?.parentTab || "leads")
          : "",
      });
      if (!payload) return;
      const normalizedCompanyToOpen = payload.openCompany;

      setCompanyDrawerOpen(false);
      setProfileTab(safeTab);
      setSelectedCompany(normalizedCompanyToOpen);
      setSelectedCompanyParent(normalizedParentCompany);
      setSelectedCompanyParentTab(
        normalizedParentCompany ? normalizeTab(options?.parentTab || "leads") : "details"
      );
      setSelectedCompanyEntityType(nextEntityType);
      setReturnListTab(listTab);
      setViewMode("leadTabsLayout");

      try {
        sessionStorage.setItem(NAV_CACHE_KEY, JSON.stringify(payload));
      } catch {
        // ignore session storage failures
      }

      navigate(
        {
          pathname: location.pathname,
          search: `?companyId=${encodeURIComponent(normalizedCompanyToOpen.id)}&tab=${encodeURIComponent(safeTab)}&entityType=${encodeURIComponent(nextEntityType)}&returnTab=${encodeURIComponent(listTab)}`,
        },
        { replace: false, state: payload }
      );
    },
    [listTab, selectedCompany, selectedCompanyEntityType, navigate, location.pathname]
  );
  const saveCompanyInfoUpdate = useCallback(
    async (company, updates = {}) => {
      const companyId = Number(
        company?.id ?? company?.company_id ?? company?.companyId ?? company?.raw?.id ?? 0
      );

      if (!Number.isFinite(companyId) || companyId <= 0) {
        throw new Error("Unable to determine which company should be updated.");
      }

      const response = await fetch(
        `${API_BASE}/api/companies/${encodeURIComponent(String(companyId))}/info`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          credentials: "include",
          body: JSON.stringify(updates),
        }
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          payload?.error ||
            payload?.message ||
            `Failed to update company (${response.status})`
        );
      }

      return payload?.company || null;
    },
    [API_BASE, authHeaders]
  );
  const updateSingleCompanyStatus = useCallback(
    async (company, status) => {
      const companyId = Number(
        company?.id ?? company?.company_id ?? company?.companyId ?? company?.raw?.id ?? 0
      );
      const nextStatus = normalizeCompanyStatusLabel(status, {
        fallback: "New",
        preserveUnknown: true,
      });

      if (!Number.isFinite(companyId) || companyId <= 0) {
        throw new Error("Unable to determine which company should be updated.");
      }

      const response = await fetch(
        `${API_BASE}/api/companies/${encodeURIComponent(String(companyId))}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          credentials: "include",
          body: JSON.stringify({ status: nextStatus }),
        }
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          payload?.error ||
            payload?.message ||
            `Failed to update company status (${response.status})`
        );
      }

      return payload?.company || null;
    },
    [API_BASE, authHeaders]
  );
  const handleOpenRowMenu = useCallback((event, row) => {
    event.stopPropagation?.();
    setRowMenuAnchorEl(event.currentTarget);
    setRowMenuCompany(row || null);
  }, []);
  const handleCloseRowMenu = useCallback(() => {
    setRowMenuAnchorEl(null);
    setRowMenuCompany(null);
  }, []);
  const hydrateCompanyForDetailView = useCallback(
    async (companyLike) => {
      const sourceRow = companyLike?.raw || companyLike || {};
      const companyId = Number(
        sourceRow?.company_id ?? sourceRow?.companyId ?? sourceRow?.id ?? companyLike?.id ?? 0
      );
      const fallbackCompany = mapCompanyRowToSelectedCompany({
        ...sourceRow,
        id: companyId || sourceRow?.id,
        company_id: companyId || sourceRow?.company_id,
        companyId: companyId || sourceRow?.companyId,
      });

      if (!Number.isFinite(companyId) || companyId <= 0) {
        return fallbackCompany;
      }

      const resolveCount = (rawCount, fallbackLength = 0, fallbackMinimum = 0) => {
        const numericValue = Number(rawCount);
        if (Number.isFinite(numericValue) && numericValue > 0) {
          return Math.max(numericValue, fallbackLength, fallbackMinimum);
        }
        return Math.max(fallbackLength, fallbackMinimum);
      };

      try {
        const response = await fetch(
          `${API_BASE}/api/companies/${encodeURIComponent(String(companyId))}`,
          {
            headers: { ...authHeaders() },
            credentials: "include",
          }
        );

        if (!response.ok) {
          const text = await response.text().catch(() => "");
          throw new Error(
            text || `Failed to fetch company detail for profile view (${response.status})`
          );
        }

        const payload = await response.json().catch(() => ({}));
        const detailCompany =
          payload?.company && typeof payload.company === "object" ? payload.company : payload;

        if (!detailCompany || typeof detailCompany !== "object") {
          return fallbackCompany;
        }

        const mergedCompany = {
          ...sourceRow,
          ...detailCompany,
          id: companyId,
          company_id: companyId,
          companyId: companyId,
          entityType: "company",
        };
        const mappedDetail = mapCompanyRowToSelectedCompany(mergedCompany);
        const contacts = Array.isArray(detailCompany?.contacts)
          ? detailCompany.contacts
          : Array.isArray(mappedDetail?.contacts)
            ? mappedDetail.contacts
            : [];
        const relatedLeads = Array.isArray(detailCompany?.related_leads)
          ? detailCompany.related_leads
          : Array.isArray(mappedDetail?.related_leads)
            ? mappedDetail.related_leads
            : [];
        const jobs = Array.isArray(detailCompany?.jobs)
          ? detailCompany.jobs
          : Array.isArray(mappedDetail?.jobs)
            ? mappedDetail.jobs
            : [];
        const activities = Array.isArray(detailCompany?.activities)
          ? detailCompany.activities
          : Array.isArray(mappedDetail?.activities)
            ? mappedDetail.activities
            : [];
        const candidates = Array.isArray(detailCompany?.candidates)
          ? detailCompany.candidates
          : Array.isArray(mappedDetail?.candidates)
            ? mappedDetail.candidates
            : [];
        const lastActivityType = cleanFilterValue(
          detailCompany?.last_activity_type ||
            detailCompany?.lastActivityType ||
            mappedDetail?.last_activity_type ||
            mappedDetail?.lastActivityType ||
            ""
        );
        const lastActivityMessage = cleanFilterValue(
          detailCompany?.last_activity_message ||
            detailCompany?.last_activity_detail ||
            detailCompany?.full_company_description ||
            mappedDetail?.lastActivityDetail ||
            mappedDetail?.notes ||
            ""
        );
        const activitiesCount = resolveCount(
          detailCompany?.activities_count ?? detailCompany?.activitiesCount,
          activities.length,
          lastActivityType || lastActivityMessage ? 1 : 0
        );
        const contactsCount = resolveCount(
          detailCompany?.contacts_count ?? detailCompany?.contactsCount,
          contacts.length
        );
        const leadsCount = resolveCount(
          detailCompany?.leads_count ?? detailCompany?.leadsCount,
          relatedLeads.length
        );
        const jobsCount = resolveCount(
          detailCompany?.jobs_count ??
            detailCompany?.jobsCount ??
            detailCompany?.job_openings ??
            detailCompany?.jobOpenings,
          jobs.length
        );
        const candidatesCount = Math.max(
          Number(detailCompany?.candidates_count ?? detailCompany?.candidatesCount ?? 0) || 0,
          candidates.length
        );

        return {
          ...mappedDetail,
          contacts,
          contactsCount,
          contacts_count: contactsCount,
          related_leads: relatedLeads,
          leadsCount,
          leads_count: leadsCount,
          jobs,
          jobsCount,
          jobs_count: jobsCount,
          jobOpenings: jobsCount,
          job_openings: jobsCount,
          candidates,
          candidatesCount,
          candidates_count: candidatesCount,
          activities,
          activitiesCount,
          activities_count: activitiesCount,
          last_activity_type: lastActivityType || mappedDetail?.last_activity_type || "",
          last_activity_message: lastActivityMessage || mappedDetail?.lastActivityDetail || "",
          contact_repair_pending: Boolean(
            detailCompany?.contact_repair_pending ?? payload?.contact_repair_pending
          ),
          company_profile_enrichment_pending: Boolean(
            detailCompany?.company_profile_enrichment_pending ??
              payload?.company_profile_enrichment_pending
          ),
          entityType: "company",
        };
      } catch (error) {
        console.error("Failed to hydrate company before opening profile view:", error);
        return fallbackCompany;
      }
    },
    [API_BASE, authHeaders]
  );
  const handleViewCompanyProfileAction = useCallback(async () => {
    const company = rowMenuCompany;
    handleCloseRowMenu();
    if (!company) return;
    const sourceRow = company?.raw || company || {};
    const normalizedEntityType = String(
      company?.entityType || sourceRow?.entityType || company?.entity_type || sourceRow?.entity_type || "company"
    ).toLowerCase();

    if (normalizedEntityType === "lead") {
      const linkedCompanyId = Number(
        sourceRow?.company_id ?? sourceRow?.companyId ?? company?.company_id ?? company?.companyId ?? 0
      );
      const originatingLeadId = Number(
        sourceRow?.lead_id ?? sourceRow?.leadId ?? sourceRow?.id ?? company?.id ?? 0
      );

      if (Number.isFinite(linkedCompanyId) && linkedCompanyId > 0) {
        const normalizedCompany = await hydrateCompanyForDetailView({
          ...sourceRow,
          id: linkedCompanyId,
          company_id: linkedCompanyId,
          companyId: linkedCompanyId,
          lead_id:
            Number.isFinite(originatingLeadId) && originatingLeadId > 0
              ? originatingLeadId
              : undefined,
          leadId:
            Number.isFinite(originatingLeadId) && originatingLeadId > 0
              ? originatingLeadId
              : undefined,
        });

        handleShowMoreCompany("details", {
          ...normalizedCompany,
          related_leads: undefined,
          leadsCount: undefined,
          leads_count: undefined,
          contacts: undefined,
          contactsCount: undefined,
          contacts_count: undefined,
          jobs: undefined,
          jobsCount: undefined,
          jobs_count: undefined,
          jobOpenings: undefined,
          job_openings: undefined,
          candidates: undefined,
          candidatesCount: undefined,
          candidates_count: undefined,
          activities: undefined,
          activitiesCount: undefined,
          activities_count: undefined,
        });
        return;
      }

      handleShowMoreCompany("details", mapLeadRowToSelectedCompany(sourceRow));
      return;
    }

    const hydratedCompany = await hydrateCompanyForDetailView(sourceRow);
    handleShowMoreCompany("details", hydratedCompany);
  }, [handleCloseRowMenu, handleShowMoreCompany, hydrateCompanyForDetailView, rowMenuCompany]);
  const handleShareCompanyAction = useCallback(async () => {
    const company = rowMenuCompany;
    handleCloseRowMenu();
    if (!company) return;

    try {
      await createAndCopyPublicShareLink({
        apiBase: API_BASE,
        payload: buildPublicSharePayload("company", company),
      });
      setToast({
        open: true,
        msg: SHARE_LINK_COPIED_MESSAGE,
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to share company:", error);
      setToast({
        open: true,
        msg: error?.message || "Failed to create share link.",
        severity: "error",
      });
    }
  }, [API_BASE, handleCloseRowMenu, rowMenuCompany]);
  const handleOpenEditCompanyDialog = useCallback(() => {
    const company = rowMenuCompany;
    handleCloseRowMenu();
    if (!company) return;

    setEditCompanyTarget(company);
    setEditCompanyError("");
    setEditCompanyForm({
      name: cleanFilterValue(company?.name || company?.company_name),
      website: cleanFilterValue(
        company?.website || company?.website_url || company?.raw?.company_website
      ),
      location: cleanFilterValue(company?.location || company?.raw?.location),
      industry: cleanFilterValue(company?.industry || company?.raw?.industry_sector),
      status: normalizeCompanyStatusLabel(company?.status, {
        fallback: "New",
        preserveUnknown: true,
      }),
    });
    setEditCompanyOpen(true);
  }, [handleCloseRowMenu, rowMenuCompany]);
  const handleEditCompanyFieldChange = useCallback(
    (field) => (event) => {
      const nextValue = event?.target?.value ?? "";
      setEditCompanyForm((prev) => ({ ...prev, [field]: nextValue }));
    },
    []
  );
  const handleSaveEditCompany = useCallback(async () => {
    const company = editCompanyTarget;
    if (!company) return;

    const nextName = cleanFilterValue(editCompanyForm.name);
    if (!nextName) {
      setEditCompanyError("Company name is required.");
      return;
    }

    const nextWebsite = cleanFilterValue(editCompanyForm.website);
    const nextLocation = cleanFilterValue(editCompanyForm.location);
    const nextIndustry = cleanFilterValue(editCompanyForm.industry);
    const currentStatus = normalizeCompanyStatusLabel(company?.status, {
      fallback: "New",
      preserveUnknown: true,
    });
    const nextStatus = normalizeCompanyStatusLabel(editCompanyForm.status, {
      fallback: currentStatus || "New",
      preserveUnknown: true,
    });

    setEditCompanySaving(true);
    setEditCompanyError("");

    try {
      await saveCompanyInfoUpdate(company, {
        name: nextName,
        company_name: nextName,
        website: nextWebsite,
        company_website: nextWebsite,
        location: nextLocation,
        industry: nextIndustry,
        industry_sector: nextIndustry,
      });

      if (normalizeInlineTextLower(nextStatus) !== normalizeInlineTextLower(currentStatus)) {
        await updateSingleCompanyStatus(company, nextStatus);
      }

      setEditCompanyOpen(false);
      setEditCompanyError("");
      setEditCompanyTarget(null);
      setEditCompanyForm({
        name: "",
        website: "",
        location: "",
        industry: "",
        status: "New",
      });
      await refreshCompanyRows().catch(() => null);
      setReloadKey((prev) => prev + 1);
      setToast({
        open: true,
        msg: `Updated ${nextName}.`,
        severity: "success",
      });
    } catch (error) {
      setEditCompanyError(error?.message || "Failed to update company.");
    } finally {
      setEditCompanySaving(false);
    }
  }, [
    editCompanyForm.industry,
    editCompanyForm.name,
    editCompanyForm.location,
    editCompanyForm.status,
    editCompanyForm.website,
    editCompanyTarget,
    refreshCompanyRows,
    saveCompanyInfoUpdate,
    updateSingleCompanyStatus,
  ]);
  const handleAddCompanyNoteAction = useCallback(() => {
    const company = rowMenuCompany;
    handleCloseRowMenu();
    if (!company) return;

    setAddNoteError("");
    setAddNoteTitle("Note");
    setAddNoteBody("");
    setAddNoteTargets([company]);
    setAddNoteOpen(true);
  }, [handleCloseRowMenu, rowMenuCompany]);
  const handleAddCompanyTagsAction = useCallback(() => {
    handleCloseRowMenu();
    setToast({
      open: true,
      msg: "Company tags will be wired into structured storage next.",
      severity: "info",
    });
  }, [handleCloseRowMenu]);
  const handleArchiveCompanyAction = useCallback(async () => {
    const company = rowMenuCompany;
    handleCloseRowMenu();
    if (!company) return;

    const companyLabel = cleanFilterValue(
      company?.name || company?.company_name || company?.raw?.company_name
    ) || "this company";
    const confirmed = window.confirm(`Archive ${companyLabel}?`);
    if (!confirmed) return;

    try {
      await updateSingleCompanyStatus(company, "Do Not Contact");
      await refreshCompanyRows().catch(() => null);
      setReloadKey((prev) => prev + 1);
      setToast({
        open: true,
        msg: `${companyLabel} archived.`,
        severity: "success",
      });
    } catch (error) {
      setToast({
        open: true,
        msg: error?.message || "Failed to archive company.",
        severity: "error",
      });
    }
  }, [handleCloseRowMenu, refreshCompanyRows, rowMenuCompany, updateSingleCompanyStatus]);
  const handleViewContactProfileAction = useCallback(() => {
    const contact = rowMenuCompany;
    handleCloseRowMenu();
    if (!contact?.id) return;
    navigate(`/contacts/${encodeURIComponent(String(contact.id))}`, {
      state: { contact, companyName: contact.entity_name },
    });
  }, [handleCloseRowMenu, navigate, rowMenuCompany]);
  const handleShareContactAction = useCallback(async () => {
    const contact = rowMenuCompany;
    handleCloseRowMenu();
    if (!contact) return;
    try {
      await createAndCopyPublicShareLink({ apiBase: API_BASE, payload: buildPublicSharePayload("contact", contact) });
      setToast({ open: true, msg: SHARE_LINK_COPIED_MESSAGE, severity: "success" });
    } catch (error) {
      setToast({ open: true, msg: error?.message || "Failed to create share link.", severity: "error" });
    }
  }, [API_BASE, handleCloseRowMenu, rowMenuCompany]);
  const handleEditContactAction = useCallback(() => {
    const contact = rowMenuCompany;
    handleCloseRowMenu();
    if (!contact?.id) return;
    navigate(`/contacts/${encodeURIComponent(String(contact.id))}`, {
      state: { contact, companyName: contact.entity_name },
    });
  }, [handleCloseRowMenu, navigate, rowMenuCompany]);
  const handleAddContactNoteAction = useCallback(() => {
    const contact = rowMenuCompany;
    handleCloseRowMenu();
    if (!contact) return;
    setAddNoteError("");
    setAddNoteTitle("Note");
    setAddNoteBody("");
    setAddNoteTargets([contact]);
    setAddNoteOpen(true);
  }, [handleCloseRowMenu, rowMenuCompany]);
  const handleAddContactTagsAction = useCallback(() => {
    handleCloseRowMenu();
    setToast({ open: true, msg: "Contact tags will be available soon.", severity: "info" });
  }, [handleCloseRowMenu]);
  const handleArchiveContactAction = useCallback(async () => {
    const contact = rowMenuCompany;
    handleCloseRowMenu();
    if (!contact?.id) return;
    const contactName = cleanFilterValue(contact.name) || "this contact";
    if (!window.confirm(`Archive ${contactName}?`)) return;
    try {
      const source = contact.raw || contact;
      const response = await fetch(`${API_BASE}/api/hiring-managers/${encodeURIComponent(String(contact.id))}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: JSON.stringify({ ...source, name: contact.name, title: contact.role, status: "Do Not Contact" }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Failed to archive contact.");
      setReloadKey((prev) => prev + 1);
      setToast({ open: true, msg: `${contactName} archived.`, severity: "success" });
    } catch (error) {
      setToast({ open: true, msg: error?.message || "Failed to archive contact.", severity: "error" });
    }
  }, [API_BASE, authHeaders, handleCloseRowMenu, rowMenuCompany]);

  const handleOpenLeadDetailFromClientView = useCallback(
    (leadRow, tab = "details", options = {}) => {
      const normalizedLead = mapLeadRowToSelectedCompany(leadRow?.raw || leadRow || {});
      const leadId = Number(normalizedLead?.id || 0);
      const fallbackParentCompany =
        String(selectedCompany?.entityType || selectedCompanyEntityType || "").toLowerCase() === "company" &&
        selectedCompany?.id
          ? buildOpenCompanyNavEntity(mapCompanyRowToSelectedCompany(selectedCompany.raw || selectedCompany), {
              entityType: "company",
            })
          : null;
      const normalizedParentCompany =
        options?.parentCompany?.id
          ? buildOpenCompanyNavEntity(mapCompanyRowToSelectedCompany(options.parentCompany), {
              entityType: "company",
            })
          : fallbackParentCompany;
      const normalizedParentTab = normalizeTab(options?.parentTab || "leads");
      const normalizedReturnListTab =
        listTab === LIST_TABS.companies ? LIST_TABS.companies : LIST_TABS.clients;

      if (!Number.isFinite(leadId) || leadId <= 0) {
        const linkedJobId = Number(
          normalizedLead?.job_id ??
            normalizedLead?.jobId ??
            normalizedLead?.related_job_id ??
            normalizedLead?.relatedJobId ??
            0
        );
        if (!Number.isFinite(linkedJobId) || linkedJobId <= 0) return;

        handleShowMoreCompany("details", normalizedLead, {
          parentCompany: normalizedParentCompany,
          parentTab: normalizedParentTab,
        });
        return;
      }

      if (normalizedParentCompany?.id) {
        const companyViewPayload = buildOpenCompanyNavPayload({
          company: normalizedParentCompany,
          defaultTab: normalizedParentTab,
          returnListTab: normalizedReturnListTab,
          entityType: "company",
        });

        setProfileTab(normalizedParentTab);

        if (companyViewPayload) {
          try {
            sessionStorage.setItem(NAV_CACHE_KEY, JSON.stringify(companyViewPayload));
          } catch {
            // ignore session storage failures
          }
        }
      }

      setCompanyDrawerOpen(false);
      navigate(
        {
          pathname: "/leads",
          search: `?leadId=${encodeURIComponent(leadId)}&tab=${encodeURIComponent(
            normalizeLeadDetailTab(tab)
          )}`,
        },
        {
          replace: false,
          state: normalizedParentCompany
            ? {
                companyOrigin: {
                  parentCompany: normalizedParentCompany,
                  parentTab: normalizedParentTab,
                  returnListTab: normalizedReturnListTab,
                },
              }
            : null,
        }
      );
    },
    [handleShowMoreCompany, listTab, navigate, selectedCompany, selectedCompanyEntityType]
  );

  const handleOpenParentCompanyFromLeadDetail = useCallback(() => {
    if (!selectedCompanyParent?.id) {
      closeSelectedCompanyView();
      return;
    }

    handleShowMoreCompany(selectedCompanyParentTab || "leads", selectedCompanyParent);
  }, [closeSelectedCompanyView, handleShowMoreCompany, selectedCompanyParent, selectedCompanyParentTab]);

  const filterCompanyRows = useCallback((activeRows, filters) => {
    let out = showingContacts ? activeRows : applyCompanyFilters(activeRows, filters);
    out = showingContacts ? out : applyAdvancedCompanyFilters(out, advancedApplied);

    if (activeStatId && activeStatId !== "total") {
      if (listTab === LIST_TABS.contacts) {
        const statMatcher = {
          hiringManager: (row) => isHiringManagerContactRow(row),
          leadContacts: (row) => normalizeHiringManagerEntityType(row?.entity_type) === "lead",
          companyContacts: (row) => {
            const entityType = normalizeHiringManagerEntityType(row?.entity_type);
            return entityType === "company" || entityType === "client";
          },
          withSource: (row) => {
            const source = String(row?.source || "").trim().toLowerCase();
            return Boolean(source) && source !== "-";
          },
        };
        const match = statMatcher[activeStatId];
        if (match) out = out.filter((row) => match(row));
      } else {
        const statMatcher = {
          companies: () => true,
          clients: (row) => isClientRow(row),
          active: (row) => isClientRow(row),
          newContacts: (row) => matchesCompanyPipelineStatus(row, "New"),
          contactedContacts: (row) => matchesCompanyPipelineStatus(row, "Contacted"),
          salesSequences: (row) =>
            buildSalesSequenceEntityKeys("company", row?.id).some((key) =>
              salesSequenceKeySet.has(key)
            ),
          leadsInDiscovery: (row) => matchesCompanyPipelineStatus(row, "Discovery"),
          proposals: (row) => matchesCompanyPipelineStatus(row, "Proposal"),
          openJobs: (row) => getRowJobOpeningsCount(row) > 0,
          teamMembers: (row) => hasAssignedOwner(row),
          website: (row) => Boolean(String(row?.website || "").trim()),
          owner: (row) => hasAssignedOwner(row),
          contact: (row) => {
            const contact = String(row?.contact || "").trim().toLowerCase();
            return Boolean(contact) && contact !== "-";
          },
          email: (row) => Boolean(String(row?.contact_email || "").trim()),
          phone: (row) => Boolean(String(row?.contact_phone || "").trim()),
          description: (row) => Boolean(String(row?.full_company_description || row?.notes || "").trim()),
        };
        const dynamicStatusId = activeStatId.startsWith("status:") ? activeStatId.slice(7) : "";
        const match = dynamicStatusId
          ? (row) => norm(row?.status) === dynamicStatusId
          : statMatcher[activeStatId] ||
            (activeStatId === "prospect" || activeStatId === "client"
              ? (row) => norm(row?.status) === activeStatId
              : null);
        if (match) out = out.filter((row) => match(row));
      }
    }

    if (overviewMetric === "new_clients") {
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
      out = out.filter((row) => {
        const timestamp = Date.parse(row?.created_at || row?.createdAt || row?.updated_at || row?.updatedAt || "");
        return Number.isFinite(timestamp) && timestamp >= thirtyDaysAgo;
      });
    }

    if (!search.trim()) return out;
    const q = search.trim().toLowerCase();

    if (showingContacts) {
      return out.filter((r) =>
        [r.name, r.role, r.entity_type, r.source, r.entity_name, r.entity_title, r.entity_website, r.email, r.phone, r.owner]
          .filter(Boolean)
          .some((v) => v.toString().toLowerCase().includes(q))
      );
    }

    return out.filter((r) =>
      [
        r.name,
        r.website,
        r.status,
        r.location,
        ...extractRowLocations(r),
        r.contact,
        r.source,
        r.owner,
        r.industry,
        r.hard_skills,
        r.soft_skills,
        r.technology_stack,
        r.recruitment_tools,
        r.salary_range,
        r.compensation,
      ]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q))
    );
  }, [advancedApplied, search, showingContacts, activeStatId, listTab, overviewMetric, salesSequenceKeySet]);

  const filteredRows = useMemo(() => filterCompanyRows(activeRows, filters),
    [filterCompanyRows, activeRows, filters]);

  const handleStatItemClick = useCallback((item) => {
    const nextId = String(item?.id || "");
    const targetTabByStat = {
      companies: LIST_TABS.companies,
      clients: LIST_TABS.clients,
      total: LIST_TABS.contacts,
    };
    const targetTab = targetTabByStat[nextId];

    // Dataset cards are navigation filters, not predicates on whichever tab
    // happens to be visible. Previously selecting Companies while viewing
    // Clients filtered the client page with a company predicate and retained
    // the client page count, producing an empty table with valid-looking page
    // numbers. Switch the dataset first and reset its server page.
    if (targetTab && targetTab !== listTab) {
      setListTab(targetTab);
      if (targetTab === LIST_TABS.contacts) setContactTablePage(0);
      else setCompanyTablePage(0);
      setActiveStatId("total");
      return;
    }

    if (!nextId || nextId === "total") {
      setActiveStatId("total");
      if (listTab === LIST_TABS.contacts) setContactTablePage(0);
      else setCompanyTablePage(0);
      return;
    }

    setCompanyTablePage(0);
    setActiveStatId((prev) => (prev === nextId ? "total" : nextId));
  }, [listTab]);

  useEffect(() => {
    setActiveStatId(
      listTab === LIST_TABS.clients && overviewMetric === "active_clients"
        ? "active"
        : "total",
    );
  }, [listTab, overviewMetric]);

  const updateFiltersFromResults = useCallback((updater) => {
    setFilters((prev) =>
      normalizeFilters(typeof updater === "function" ? updater(prev) : updater)
    );
    setCompanyTablePage(0);
    setContactTablePage(0);
  }, []);

  const handleRemoveFilterPill = useCallback(
    (field, value) => {
      updateFiltersFromResults((prev) => {
        if (field === "ai") {
          return { ...prev, ai: "" };
        }

        return {
          ...prev,
          [field]: removeFilterValueFromList(prev[field], value),
        };
      });
    },
    [updateFiltersFromResults]
  );

  const resultsFilterPills = useMemo(() => {
    const pills = [];

    const appendTextPill = (field, value, prefix = "") => {
      const label = cleanFilterValue(value);
      if (!label) return;

      pills.push({
        key: `${field}:${norm(label)}`,
        label: `${prefix}${label}`,
        onDelete: () => handleRemoveFilterPill(field, label),
      });
    };

    const appendArrayPills = (field, values = []) => {
      (values || []).forEach((value) => {
        const label = cleanFilterValue(value);
        if (!label) return;

        pills.push({
          key: `${field}:${norm(label)}`,
          label,
          onDelete: () => handleRemoveFilterPill(field, label),
        });
      });
    };

    appendTextPill("ai", filters.ai, "AI: ");
    appendArrayPills("companyNames", filters.companyNames);
    appendArrayPills("techKeywords", filters.techKeywords);
    appendArrayPills("locations", filters.locations);
    appendArrayPills("owners", filters.owners);
    appendArrayPills("statuses", filters.statuses);
    appendArrayPills("activities", filters.activities);
    appendArrayPills("sources", filters.sources);

    return pills;
  }, [filters, handleRemoveFilterPill]);

  const selectedIdSetEffective = useMemo(() => {
    const ids = selectedIdsLocal.length ? selectedIdsLocal : selectedIds;
    return new Set((ids || []).map((x) => String(x)));
  }, [selectedIdsLocal, selectedIds]);

  const selectedRowsEffective = useMemo(() => {
    return activeRows.filter((r) => selectedIdSetEffective.has(String(r.id)));
  }, [activeRows, selectedIdSetEffective]);

  const selectedBulkActionTargets = useMemo(() => {
    if (!selectedRowsEffective.length) return [];
    if (!showingContacts) return selectedRowsEffective.filter(Boolean);

    const companyRowMap = new Map(companyRows.map((row) => [String(row.id), row]));
    const leadRowMap = new Map(leadRows.map((row) => [String(row.id), row]));
    const seen = new Set();

    return selectedRowsEffective.reduce((next, row) => {
      const entityType = normalizeHiringManagerEntityType(row?.entity_type);
      const entityId = String(row?.entity_id ?? "");
      const targetRow = entityType === "lead" ? leadRowMap.get(entityId) : companyRowMap.get(entityId);

      if (!targetRow) return next;

      const targetKey = `${String(targetRow?.entityType || entityType || "company").toLowerCase()}:${String(targetRow.id)}`;
      if (seen.has(targetKey)) return next;

      seen.add(targetKey);
      next.push(targetRow);
      return next;
    }, []);
  }, [companyRows, leadRows, selectedRowsEffective, showingContacts]);

  const selectedCount = selectedRowsEffective.length;
  const selectedCompanyActionTarget = useMemo(() => {
    if (selectedRowsEffective.length !== 1) return null;

    const candidate = selectedRowsEffective[0];
    const entityType = String(
      candidate?.entityType || candidate?.entity_type || "company"
    ).toLowerCase();

    return entityType === "company" ? candidate : null;
  }, [selectedRowsEffective]);
  const selectedSequenceItems = useMemo(
    () => selectedRowsEffective.map((row) => buildClientSequenceItem(row)),
    [selectedRowsEffective]
  );
  const resetAddContactForm = useCallback(() => {
    setAddContactForm({
      name: "",
      role: "",
      email: "",
      phone: "",
      linkedin_url: "",
      source: "Manual",
    });
    setAddContactError("");
    setAddContactTarget(null);
  }, []);
  const closeAddContactDialog = useCallback(() => {
    if (addContactSaving) return;
    setAddContactOpen(false);
    resetAddContactForm();
  }, [addContactSaving, resetAddContactForm]);
  const closeAddNoteDialog = useCallback(() => {
    if (addNoteSaving) return;
    setAddNoteOpen(false);
    setAddNoteError("");
    setAddNoteTitle("Note");
    setAddNoteBody("");
    setAddNoteTargets([]);
  }, [addNoteSaving]);
  const resetEditCompanyState = useCallback(() => {
    setEditCompanyError("");
    setEditCompanyTarget(null);
    setEditCompanyForm({
      name: "",
      website: "",
      location: "",
      industry: "",
      status: "New",
    });
  }, []);
  const closeEditCompanyDialog = useCallback(() => {
    if (editCompanySaving) return;
    setEditCompanyOpen(false);
    resetEditCompanyState();
  }, [editCompanySaving, resetEditCompanyState]);
  const buildSelectedCompanyLeadSeed = useCallback(() => {
    if (!selectedCompanyActionTarget) return null;

    const companyName = cleanFilterValue(
      selectedCompanyActionTarget?.name ||
        selectedCompanyActionTarget?.company_name ||
        selectedCompanyActionTarget?.companyName ||
        selectedCompanyActionTarget?.raw?.company_name
    );
    const website = cleanFilterValue(
      selectedCompanyActionTarget?.website ||
        selectedCompanyActionTarget?.website_url ||
        selectedCompanyActionTarget?.raw?.company_website ||
        selectedCompanyActionTarget?.raw?.website
    );
    const location = cleanFilterValue(
      selectedCompanyActionTarget?.location || selectedCompanyActionTarget?.raw?.location
    );
    const companyId = Number(
      selectedCompanyActionTarget?.company_id ??
        selectedCompanyActionTarget?.companyId ??
        selectedCompanyActionTarget?.raw?.company_id ??
        selectedCompanyActionTarget?.id ??
        0
    );

    return {
      companyName,
      company_name: companyName,
      companyId: Number.isFinite(companyId) && companyId > 0 ? companyId : null,
      company_id: Number.isFinite(companyId) && companyId > 0 ? companyId : undefined,
      website: website || "",
      location: location || "",
    };
  }, [selectedCompanyActionTarget]);
  const handleOpenAddLeadAction = useCallback(() => {
    if (!selectedCompanyActionTarget?.id) {
      setToast({
        open: true,
        msg: "Select exactly 1 company first.",
        severity: "info",
      });
      return;
    }

    // This is the company-list shortcut for adding a contact. Reuse the
    // standard contact flow used by the global Add menu so both paths have
    // the same form and validation, with the selected company prefilled.
    setCompanyLeadDialogMode("contact");
    setCompanyLeadDialogValue(buildSelectedCompanyLeadSeed());
    setCompanyLeadDialogOpen(true);
  }, [buildSelectedCompanyLeadSeed, selectedCompanyActionTarget]);
  const handleCloseCompanyLeadDialog = useCallback(() => {
    setCompanyLeadDialogOpen(false);
    setCompanyLeadDialogMode("lead");
    setCompanyLeadDialogValue(null);
  }, []);
  const handleCompanyLeadDialogSubmit = useCallback((form, createdContact) => {
    setReloadKey((prev) => prev + 1);

    const companyId = Number(
      createdContact?.entity_id ?? createdContact?.entityId ?? form?.companyId ?? 0
    );
    if (typeof window !== "undefined" && Number.isFinite(companyId) && companyId > 0) {
      window.dispatchEvent(
        new CustomEvent("company-contact-created", {
          detail: { companyId, contact: createdContact || null },
        })
      );
    }

    return undefined;
  }, []);
  const handleOpenAddContactAction = useCallback(() => {
    if (!selectedCompanyActionTarget?.id) {
      setToast({
        open: true,
        msg: "Select exactly 1 company first.",
        severity: "info",
      });
      return;
    }

    setAddContactTarget(selectedCompanyActionTarget);
    setAddContactError("");
    setAddContactForm({
      name: "",
      role: "",
      email: "",
      phone: "",
      linkedin_url: "",
      source: "Manual",
    });
    setAddContactOpen(true);
  }, [selectedCompanyActionTarget]);
  const handleAddContactFieldChange = useCallback(
    (field) => (event) => {
      const nextValue = event?.target?.value ?? "";
      setAddContactForm((prev) => ({ ...prev, [field]: nextValue }));
    },
    []
  );
  const handleSaveAddContact = useCallback(async () => {
    const name = cleanFilterValue(addContactForm.name);
    const entityId = Number(
      addContactTarget?.company_id ??
        addContactTarget?.companyId ??
        addContactTarget?.raw?.company_id ??
        addContactTarget?.id ??
        0
    );

    if (!name) {
      setAddContactError("Contact name is required.");
      return;
    }

    if (!Number.isFinite(entityId) || entityId <= 0) {
      setAddContactError("Unable to determine which company should receive this contact.");
      return;
    }

    setAddContactSaving(true);
    setAddContactError("");

    try {
      const response = await fetch(`${API_BASE}/api/hiring-managers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        credentials: "include",
        body: JSON.stringify({
          entityType: "company",
          entityId,
          contact_kind: "company_contact",
          name,
          role: cleanFilterValue(addContactForm.role),
          email: cleanFilterValue(addContactForm.email),
          phone: cleanFilterValue(addContactForm.phone),
          linkedin_url: cleanFilterValue(addContactForm.linkedin_url),
          source: cleanFilterValue(addContactForm.source) || "Manual",
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          payload?.error ||
            payload?.message ||
            `Failed to create contact (${response.status})`
        );
      }

      closeAddContactDialog();
      setReloadKey((prev) => prev + 1);
      setToast({
        open: true,
        msg: `Contact added to ${
          cleanFilterValue(
            addContactTarget?.name ||
              addContactTarget?.company_name ||
              addContactTarget?.raw?.company_name
          ) || "company"
        }.`,
        severity: "success",
      });
    } catch (error) {
      setAddContactError(error?.message || "Failed to create contact.");
    } finally {
      setAddContactSaving(false);
    }
  }, [API_BASE, addContactForm, addContactTarget, authHeaders, closeAddContactDialog]);
  const describeSelection = useCallback((companyCount, leadCount) => {
    const total = Number(companyCount || 0) + Number(leadCount || 0);
    if (!total) {
      return {
        total: 0,
        singular: showingCompanies ? "company" : "client",
        plural: showingCompanies ? "companies" : "clients",
      };
    }

    if (companyCount && leadCount) {
      return { total, singular: "record", plural: "records" };
    }

    if (companyCount) {
      return { total, singular: "company", plural: "companies" };
    }

    return { total, singular: "client", plural: "clients" };
  }, [showingCompanies]);
  const handleOpenAddNoteAction = useCallback(() => {
    if (!selectedBulkActionTargets.length) {
      setToast({
        open: true,
        msg: "Select at least 1 item first.",
        severity: "info",
      });
      return;
    }

    setAddNoteError("");
    setAddNoteTitle("Note");
    setAddNoteBody("");
    setAddNoteTargets(selectedBulkActionTargets);
    setAddNoteOpen(true);
  }, [selectedBulkActionTargets]);
  const handleSaveAddNote = useCallback(async () => {
    const noteTitle = cleanFilterValue(addNoteTitle) || "Note";
    const noteBody = cleanFilterValue(addNoteBody);

    if (!addNoteTargets.length) {
      setAddNoteError("Select at least one company before adding a note.");
      return;
    }

    if (!noteBody) {
      setAddNoteError("Note text is required.");
      return;
    }

    setAddNoteSaving(true);
    setAddNoteError("");

    try {
      await Promise.all(
        addNoteTargets.map(async (row) => {
          const rawEntityType = String(row?.entityType || row?.entity_type || "company").toLowerCase();
          const entityType = ["lead", "contact"].includes(rawEntityType)
            ? rawEntityType
            : "company";
          const entityId = Number(
            row?.id ?? row?.company_id ?? row?.lead_id ?? row?.contact_id ?? row?.raw?.id ?? 0
          );

          if (!Number.isFinite(entityId) || entityId <= 0) return;

          const response = await fetch(`${API_BASE}/api/activities`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...authHeaders(),
            },
            credentials: "include",
            body: JSON.stringify({
              entityType,
              entityId,
              activityType: "note",
              title: noteTitle,
              summary: noteBody,
              details: noteBody,
            }),
          });

          const payload = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(
              payload?.error ||
                payload?.message ||
                `Failed to add note (${response.status})`
            );
          }
        })
      );

      closeAddNoteDialog();
      setReloadKey((prev) => prev + 1);
      const contactCount = addNoteTargets.filter(
        (row) => String(row?.entityType || row?.entity_type || "").toLowerCase() === "contact"
      ).length;
      const companyCount = addNoteTargets.filter(
        (row) => String(row?.entityType || row?.entity_type || "").toLowerCase() === "company"
      ).length;
      const leadCount = Math.max(0, addNoteTargets.length - companyCount - contactCount);
      const selectionMeta = contactCount
        ? { total: addNoteTargets.length, singular: "contact", plural: "contacts" }
        : describeSelection(companyCount, leadCount);
      setToast({
        open: true,
        msg: `Added a note to ${selectionMeta.total} ${
          selectionMeta.total === 1 ? selectionMeta.singular : selectionMeta.plural
        }.`,
        severity: "success",
      });
    } catch (error) {
      setAddNoteError(error?.message || "Failed to add note.");
    } finally {
      setAddNoteSaving(false);
    }
  }, [API_BASE, addNoteBody, addNoteTargets, addNoteTitle, authHeaders, closeAddNoteDialog, describeSelection]);
  const topStats = useMemo(() => {
    if (listTab === LIST_TABS.contacts) {
      const total = enrichedContactRows.length;
      const hiringManagerCount = enrichedContactRows.filter((row) =>
        isHiringManagerContactRow(row)
      ).length;
      const leadContacts = enrichedContactRows.filter(
        (row) => normalizeHiringManagerEntityType(row?.entity_type) === "lead"
      ).length;
      const companyContacts = enrichedContactRows.filter((row) => {
        const entityType = normalizeHiringManagerEntityType(row?.entity_type);
        return entityType === "company" || entityType === "client";
      }).length;
      const withSource = enrichedContactRows.filter((r) => norm(r.source) && norm(r.source) !== "-").length;

      return [
        {
          id: "total",
          value: String(Number.isFinite(Number(contactRowsTotal)) ? contactRowsTotal : total),
          label: "Total Contacts",
        },
        { id: "hiringManager", value: String(hiringManagerCount), label: "Hiring Managers" },
        { id: "leadContacts", value: String(leadContacts), label: "Lead Contacts" },
        { id: "companyContacts", value: String(companyContacts), label: "Company Contacts" },
        { id: "withSource", value: String(withSource), label: "With Source" },
      ];
    }

    const newContactsCount = companiesTabRows.filter((row) =>
      matchesCompanyPipelineStatus(row, "New")
    ).length;
    const contactedContactsCount = companiesTabRows.filter((row) =>
      matchesCompanyPipelineStatus(row, "Contacted")
    ).length;
    const discoveryCount = companiesTabRows.filter((row) =>
      matchesCompanyPipelineStatus(row, "Discovery")
    ).length;
    const proposalCount = companiesTabRows.filter((row) =>
      matchesCompanyPipelineStatus(row, "Proposal")
    ).length;
    // Every stat below represents Company rows, so count the same row-level
    // predicates used by the active-stat table filter (rather than related
    // sequence/job/team records).
    const salesSequencesCount = companyRows.filter((row) =>
      buildSalesSequenceEntityKeys("company", row?.id).some((key) =>
        salesSequenceKeySet.has(key)
      )
    ).length;
    const openJobsCount = companyRows.filter(
      (row) => getRowJobOpeningsCount(row) > 0
    ).length;
    const teamMembersCount = companyRows.filter((row) => hasAssignedOwner(row)).length;

    return [
      {
        id: "companies",
        value: String(
          Number.isFinite(Number(companyRowsTotal)) ? companyRowsTotal : companiesTabRows.length
        ),
        label: "Companies",
      },
      {
        id: "clients",
        value: String(Number.isFinite(Number(clientRowsTotal)) ? clientRowsTotal : clientRows.length),
        label: "Clients",
      },
      { id: "newContacts", value: String(newContactsCount), label: "New Contacts" },
      {
        id: "contactedContacts",
        value: String(contactedContactsCount),
        label: "Contacted Contacts",
      },
      { id: "salesSequences", value: String(salesSequencesCount), label: "Sales Sequences" },
      { id: "leadsInDiscovery", value: String(discoveryCount), label: "Leads in Discovery" },
      { id: "proposals", value: String(proposalCount), label: "Proposals" },
      { id: "openJobs", value: String(openJobsCount), label: "Open Jobs" },
      { id: "teamMembers", value: String(teamMembersCount), label: "Team Members" },
    ];
  }, [
    clientRows.length,
    clientRowsTotal,
    companiesTabRows,
    companyRows,
    companyRowsTotal,
    contactRowsTotal,
    enrichedContactRows,
    listTab,
    salesSequenceKeySet,
  ]);
  const tableResetSignal = useMemo(
    () =>
      JSON.stringify({
        listTab,
        search,
        activeStatId,
        filters,
        advancedApplied,
      }),
    [activeStatId, advancedApplied, filters, listTab, search]
  );
  const handleContactServerPageChange = useCallback((nextPage) => {
    setContactTablePage(Math.max(0, Number(nextPage) || 0));
  }, []);
  const handleContactServerRowsPerPageChange = useCallback((nextRowsPerPage) => {
    setContactTableRowsPerPage(Math.max(1, Number(nextRowsPerPage) || 10));
    setContactTablePage(0);
  }, []);

  const handleCompanyServerPageChange = useCallback((nextPage) => {
    setCompanyTablePage(Math.max(0, Number(nextPage) || 0));
  }, []);

  const handleCompanyServerRowsPerPageChange = useCallback((nextRowsPerPage) => {
    setCompanyTableRowsPerPage(Math.max(1, Number(nextRowsPerPage) || 10));
    setCompanyTablePage(0);
  }, []);

  const activeStatusOptions = COMPANY_STATUS_OPTIONS;
  const showSelectCompanyMessage = useCallback(() => {
    setStatusToast({
      open: true,
      message: "Please select an item to change status.",
      severity: "info",
    });
  }, []);

  const handleOpenAddToSequence = useCallback(() => {
    if (!selectedCount) {
      setToast({
        open: true,
        msg: "Select at least 1 item first.",
        severity: "info",
      });
      return;
    }
    setIsAddToSequenceOpen(true);
  }, [selectedCount]);

  const handleCloseAgentDialog = useCallback(() => {
    setIsAgentDialogOpen(false);
    setAgentDialogInitialPrompt("");
    setAgentDialogSubmitOnOpen(false);
  }, []);

  const handleAssignSelectionPerson = useCallback(
    async (member, { includeOwnerUserId = true, successRoleLabel = "team member" } = {}) => {
      const assigneeName = cleanFilterValue(member?.name);
      const assigneeUserId =
        includeOwnerUserId && Number.isFinite(Number(member?.id)) && Number(member.id) > 0
          ? Number(member.id)
          : null;
      const companyIds = selectedRowsEffective
        .filter((row) => String(row?.entityType || "").toLowerCase() === "company")
        .map((row) => Number(row?.id))
        .filter((value) => Number.isFinite(value) && value > 0);
      const leadIds = selectedRowsEffective
        .filter((row) => String(row?.entityType || "").toLowerCase() !== "company")
        .map((row) => Number(row?.id))
        .filter((value) => Number.isFinite(value) && value > 0);

      if (!assigneeName || (!companyIds.length && !leadIds.length)) return;

      if (companyIds.length) {
        const res = await fetch(`${API_BASE}/api/companies/bulk-assign-team-member`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          credentials: "include",
          body: JSON.stringify({
            ids: companyIds,
            assignee_user_id: assigneeUserId,
            assignee_name: assigneeName,
          }),
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(text || `Failed to assign companies (${res.status})`);
        }
      }

      if (leadIds.length) {
        const res = await fetch(`${API_BASE}/api/leads/bulk-assign-team-member`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          credentials: "include",
          body: JSON.stringify({
            ids: leadIds,
            assignee_user_id: assigneeUserId,
            assignee_name: assigneeName,
          }),
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(text || `Failed to assign clients (${res.status})`);
        }
      }

      if (companyIds.length) {
        const companyIdSet = new Set(companyIds.map((value) => String(value)));
        setCompanyRows((prev) =>
          prev.map((row) =>
            companyIdSet.has(String(row?.id))
              ? {
                  ...row,
                  owner: assigneeName || row.owner,
                  owner_name: assigneeName || row.owner_name,
                  team_member: assigneeName || row.team_member,
                }
              : row
          )
        );
      }

      if (leadIds.length) {
        const leadIdSet = new Set(leadIds.map((value) => String(value)));
        setLeadRows((prev) =>
          prev.map((row) =>
            leadIdSet.has(String(row?.id))
              ? {
                  ...row,
                  owner: assigneeName || row.owner,
                  owner_name: assigneeName || row.owner_name,
                }
              : row
          )
        );
      }

      if (companyIds.length) {
        try {
          await refreshCompanyRows();
        } catch (refreshError) {
          console.error("Failed to refresh company rows after team assignment:", refreshError);
        }
      }

      const selectedIdSet = new Set([...companyIds, ...leadIds].map((value) => String(value)));
      setSelectedCompany((prev) =>
        prev && selectedIdSet.has(String(prev?.id))
          ? {
              ...prev,
              owner: assigneeName || prev.owner,
              owner_name: assigneeName || prev.owner_name,
              team_member: assigneeName || prev.team_member,
            }
          : prev
      );

      const selectionMeta = describeSelection(companyIds.length, leadIds.length);
      setToast({
        open: true,
        msg: `Assigned ${successRoleLabel} for ${selectionMeta.total} ${selectionMeta.total === 1 ? selectionMeta.singular : selectionMeta.plural} to ${assigneeName}.`,
        severity: "success",
      });
    },
    [API_BASE, authHeaders, describeSelection, refreshCompanyRows, selectedRowsEffective]
  );

  const handleAssignOwner = useCallback(
    async (member) =>
      handleAssignSelectionPerson(member, {
        includeOwnerUserId: true,
        successRoleLabel: "owner",
      }),
    [handleAssignSelectionPerson]
  );

  const handleAssignTeamMember = useCallback(
    async (member) =>
      handleAssignSelectionPerson(member, {
        includeOwnerUserId: false,
        successRoleLabel: "team member",
      }),
    [handleAssignSelectionPerson]
  );

  const handleApplyStatus = useCallback(
    async (status, { skipActivityLog = false } = {}) => {
      const nextStatus = String(status || "").trim();
      if (!nextStatus || statusSaving) return;
      if (!selectedCount) {
        showSelectCompanyMessage();
        return;
      }

      const companyIds = selectedRowsEffective
        .filter((row) => String(row?.entityType || "").toLowerCase() === "company")
        .map((row) => Number(row?.id))
        .filter((value) => Number.isFinite(value) && value > 0);
      const leadIds = selectedRowsEffective
        .filter((row) => String(row?.entityType || "").toLowerCase() !== "company")
        .map((row) => Number(row?.id))
        .filter((value) => Number.isFinite(value) && value > 0);

      setStatusSaving(true);
      try {
        if (companyIds.length) {
          const res = await fetch(`${API_BASE}/api/companies/bulk-status`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", ...authHeaders() },
            credentials: "include",
            body: JSON.stringify({
              ids: companyIds,
              status: nextStatus,
              skip_activity_log: Boolean(skipActivityLog),
            }),
          });

          if (!res.ok) {
            const txt = await res.text().catch(() => "");
            throw new Error(txt || "Failed to update company status");
          }
        }

        if (leadIds.length) {
          const res = await fetch(`${API_BASE}/api/leads/bulk-status`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", ...authHeaders() },
            credentials: "include",
            body: JSON.stringify({ ids: leadIds, status: nextStatus }),
          });

          if (!res.ok) {
            const txt = await res.text().catch(() => "");
            throw new Error(txt || "Failed to update client status");
          }
        }

        if (companyIds.length) {
          const companyIdSet = new Set(companyIds.map((id) => String(id)));
          setCompanyRows((prev) =>
            prev.map((row) => (companyIdSet.has(String(row.id)) ? { ...row, status: nextStatus } : row))
          );
          try {
            await refreshCompanyRows();
          } catch (refreshError) {
            console.error("Failed to refresh company rows after status update:", refreshError);
          }
        }

        if (leadIds.length) {
          const leadIdSet = new Set(leadIds.map((id) => String(id)));
          setLeadRows((prev) =>
            prev.map((row) => (leadIdSet.has(String(row.id)) ? { ...row, status: nextStatus } : row))
          );
        }

        const selectedIdSet = new Set([...companyIds, ...leadIds].map((id) => String(id)));
        setSelectedCompany((prev) => {
          if (!prev || !selectedIdSet.has(String(prev.id))) return prev;
          return { ...prev, status: nextStatus };
        });
        setSelectedIds([]);
        setSelectedIdsLocal([]);
        // Reload both company and lead sources so the active filtered table
        // and its result count reflect the persisted status changes.
        setReloadKey((prev) => prev + 1);

        const selectionMeta = describeSelection(companyIds.length, leadIds.length);
        setStatusToast({
          open: true,
          message: `Updated ${selectionMeta.total} ${selectionMeta.total === 1 ? selectionMeta.singular : selectionMeta.plural} to "${nextStatus}".`,
          severity: "success",
        });
      } catch (err) {
        console.error(err);
        setStatusToast({
          open: true,
          message: err?.message || "Error updating status",
          severity: "error",
        });
      } finally {
        setStatusSaving(false);
      }
    },
    [
      API_BASE,
      authHeaders,
      describeSelection,
      selectedCount,
      selectedRowsEffective,
      showSelectCompanyMessage,
      statusSaving,
      refreshCompanyRows,
    ]
  );

  const handleArchiveSelected = useCallback(async () => {
    await handleApplyStatus("Do Not Contact");
  }, [handleApplyStatus]);

  const handleConvertToClient = useCallback(async () => {
    // The conversion is complete only after the status update succeeds; log
    // that submitted action in the entity's Activities tab.
    await handleApplyStatus(ACTIVE_CLIENT_COMPANY_STATUS);
  }, [handleApplyStatus]);

  const handleLaunchAgentTask = useCallback(
    async (taskValue) => {
      if (taskValue === "convertToClient") {
        await handleConvertToClient();
        return;
      }

      const selectedRows = selectedBulkActionTargets.filter(Boolean);

      const selectedTask =
        COMPANY_AGENT_TASK_OPTIONS.find((option) => option.value === taskValue) || null;
      const taskLabel = selectedTask?.label || "Launch Agent";

      setAgentDialogInitialPrompt(
        buildCompanyAgentTaskPrompt({
          taskValue,
          rows: selectedRows,
        })
      );
      setAgentDialogSubmitOnOpen(true);
      setIsAgentDialogOpen(true);
      const companyCount = selectedRows.filter(
        (row) => String(row?.entityType || "").toLowerCase() === "company"
      ).length;
      const leadCount = Math.max(0, selectedRows.length - companyCount);
      const selectionMeta = describeSelection(companyCount, leadCount);
      setToast({
        open: true,
        msg: selectionMeta.total
          ? `Launching ${taskLabel} for ${selectionMeta.total} selected ${
              selectionMeta.total === 1 ? selectionMeta.singular : selectionMeta.plural
            }.`
          : `Opening ${taskLabel}.`,
        severity: "success",
      });
    },
    [describeSelection, handleConvertToClient, selectedBulkActionTargets]
  );
  const handleLaunchCompanyAgent = useCallback(() => {
    handleLaunchAgentTask("companyResearch");
  }, [handleLaunchAgentTask]);
  const handleOpenTopBarCompanyAgent = useCallback(() => {
    setAgentDialogInitialPrompt("");
    setAgentDialogSubmitOnOpen(false);
    setIsAgentDialogOpen(true);
  }, []);
  const handleCreateCompanyProposal = useCallback(() => {
    handleLaunchAgentTask("createProposal");
  }, [handleLaunchAgentTask]);

  const handleExportSelected = useCallback(() => {
    const rowsToExport = selectedRowsEffective.length ? selectedRowsEffective : filteredRows;
    if (!rowsToExport.length) {
      setToast({
        open: true,
        msg: `No ${showingCompanies ? "companies" : "clients"} to export.`,
        severity: "info",
      });
      return;
    }

    const header = ["Name", "Status", "Website", "Location", "Contact", "Source", "Owner"];
    const csvRows = rowsToExport.map((row) => [
      row.name,
      row.status,
      row.website,
      row.location,
      row.contact,
      row.source,
      row.owner,
    ]);

    downloadCsvFile(
      `${showingCompanies ? "companies" : "clients"}-${new Date().toISOString().slice(0, 10)}.csv`,
      [header, ...csvRows]
    );
    setToast({
      open: true,
      msg: `Exported ${rowsToExport.length} ${showingCompanies ? (rowsToExport.length > 1 ? "companies" : "company") : rowsToExport.length > 1 ? "clients" : "client"}.`,
      severity: "success",
    });
  }, [filteredRows, selectedRowsEffective, showingCompanies]);

  const handleBulkDelete = useCallback(async (_event, options = {}) => {
    if (!isAdminUser) {
      setToast({
        open: true,
        msg: "Only admins can delete companies.",
        severity: "error",
      });
      return;
    }

    if (!selectedCount) {
      const selectionMeta = describeSelection(0, 0);
      setToast({
        open: true,
        msg: `Select at least 1 ${selectionMeta.singular} first.`,
        severity: "info",
      });
      return;
    }

    const companyIdsToDelete = selectedRowsEffective
      .filter((row) => String(row?.entityType || "").toLowerCase() === "company")
      .map((row) => Number(row?.id))
      .filter((value) => Number.isFinite(value) && value > 0);
    const leadIdsToDelete = selectedRowsEffective
      .filter((row) => String(row?.entityType || "").toLowerCase() !== "company")
      .map((row) => Number(row?.id))
      .filter((value) => Number.isFinite(value) && value > 0);
    const totalToDelete = companyIdsToDelete.length + leadIdsToDelete.length;

    if (!totalToDelete) return;

    const selectionMeta = describeSelection(companyIdsToDelete.length, leadIdsToDelete.length);

    try {
      let deletedCompanyIds = [];
      let cascadeDeletedLeadIds = [];

      if (companyIdsToDelete.length) {
        const res = await fetch(`${API_BASE}/api/companies/bulk-delete`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          credentials: "include",
          body: JSON.stringify({ ids: companyIdsToDelete }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(payload?.error || "Failed to delete companies.");
        }
        notifyCompanyRelationshipsChanged();

        deletedCompanyIds = Array.isArray(payload?.deleted_company_ids)
          ? payload.deleted_company_ids
              .map((value) => Number(value))
              .filter((value) => Number.isFinite(value) && value > 0)
          : companyIdsToDelete;
        cascadeDeletedLeadIds = Array.isArray(payload?.deleted_lead_ids)
          ? payload.deleted_lead_ids
              .map((value) => Number(value))
              .filter((value) => Number.isFinite(value) && value > 0)
          : [];
      }

      if (leadIdsToDelete.length) {
        const res = await fetch(`${API_BASE}/api/leads/bulk-delete`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          credentials: "include",
          body: JSON.stringify({ ids: leadIdsToDelete }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(payload?.error || "Failed to delete clients.");
        }
        notifyCompanyRelationshipsChanged();
      }

      const allDeletedCompanyIds = deletedCompanyIds.length ? deletedCompanyIds : companyIdsToDelete;
      const allDeletedLeadIds = Array.from(
        new Set([...leadIdsToDelete, ...cascadeDeletedLeadIds].map((value) => Number(value)))
      ).filter((value) => Number.isFinite(value) && value > 0);

      if (allDeletedCompanyIds.length) {
        const companyIdSet = new Set(allDeletedCompanyIds.map((value) => String(value)));
        setCompanyRows((prev) => prev.filter((row) => !companyIdSet.has(String(row?.id))));
      }

      if (allDeletedLeadIds.length) {
        const leadIdSet = new Set(allDeletedLeadIds.map((value) => String(value)));
        setLeadRows((prev) => prev.filter((row) => !leadIdSet.has(String(row?.id))));
      }

      if (allDeletedCompanyIds.length || allDeletedLeadIds.length) {
        const companyIdSet = new Set(allDeletedCompanyIds.map((value) => String(value)));
        const leadIdSet = new Set(allDeletedLeadIds.map((value) => String(value)));

        setContactRows((prev) =>
          prev.filter((row) => {
            const entityType = String(row?.entity_type || "").toLowerCase();
            const entityId = String(row?.entity_id || "");

            if ((entityType === "company" || entityType === "client") && companyIdSet.has(entityId)) {
              return false;
            }
            if (entityType === "lead" && leadIdSet.has(entityId)) {
              return false;
            }
            return true;
          })
        );
      }

      setSelectedIds([]);
      setSelectedIdsLocal([]);
      const selectedIdSet = new Set(
        [...allDeletedCompanyIds, ...allDeletedLeadIds].map((value) => String(value))
      );
      setSelectedCompany((prev) => (prev && selectedIdSet.has(String(prev?.id)) ? null : prev));
      setCompanyDrawerOpen((prev) =>
        selectedCompany && selectedIdSet.has(String(selectedCompany?.id)) ? false : prev
      );

      if (allDeletedCompanyIds.length) {
        try {
          await refreshCompanyRows();
        } catch (refreshError) {
          console.error("Failed to refresh company rows after delete:", refreshError);
        }
      }

      setToast({
        open: true,
        msg: `Deleted ${totalToDelete} ${totalToDelete === 1 ? selectionMeta.singular : selectionMeta.plural}.`,
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      setToast({
        open: true,
        msg: error?.message || `Failed to delete ${selectionMeta.plural}.`,
        severity: "error",
      });
    }
  }, [
    API_BASE,
    authHeaders,
    describeSelection,
    refreshCompanyRows,
    isAdminUser,
    selectedCount,
    selectedRowsEffective,
    selectedCompany,
  ]);

  const contactColumns = useMemo(
    () => [
      {
        id: "name",
        label: "Name",
        minWidth: 190,
        sortable: true,
        renderCell: (row) => (
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            onClick={() => navigate(`/contacts/${encodeURIComponent(String(row.id))}`, { state: { contact: row, companyName: row.entity_name } })}
            sx={{ cursor: "pointer" }}
          >
            <Avatar
              src={row.profile_image || undefined}
              sx={{
                width: 30,
                height: 30,
                fontSize: 14,
                background: row.profile_image
                  ? "transparent"
                  : "linear-gradient(135deg,#F4EBFF,#FCE7F3)",
                color: "#7F56D9",
              }}
            >
              {!row.profile_image ? getPersonInitials(row.name) : null}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {row.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.35,
                  width: 200,
                  maxWidth: 200,
                  color: "#98A2B3",
                  lineHeight: 1.2,
                }}
                noWrap
              >
                {row.role || "Hiring Manager"}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        id: "entity_name",
        label: "Company Name",
        minWidth: 190,
        sortable: true,
        renderCell: (row) => <CompanyTableCell row={row} />,
      },
      {
        id: "status",
        label: "Status",
        minWidth: 118,
        sortable: true,
        renderCell: (row) => <Chip label={row.status || "New"} size="small" sx={getCompanyStatusBadgeSx(row.status)} />,
      },
      { id: "role", label: "Role", minWidth: 160, sortable: true },
      {
        id: "source",
        label: "Source",
        minWidth: 130,
        sortable: true,
        renderCell: (row) => (
          <Typography variant="body2" sx={{ color: "#475467" }} noWrap>
            {row.owner || "-"}
          </Typography>
        ),
      },
      {
        id: "contact_information",
        label: "Contact information",
        minWidth: 120,
        sortable: false,
        renderCell: (row) => (
          <InfoActionIcons
            showDisabled
            showGroupDivider={false}
            preserveOrder
            gap={0.8}
            iconSize={18}
            actions={buildContactInfoActions(row)}
          />
        ),
      },
      {
        id: "actions",
        label: "Actions",
        minWidth: 64,
        sortable: false,
        renderCell: (row) => (
          <IconButton
            size="small"
            aria-label={`Open actions for ${row?.name || "contact"}`}
            onClick={(event) => handleOpenRowMenu(event, row)}
            sx={{ color: "#98A2B3" }}
          >
            <MoreVertIcon sx={{ fontSize: 18, color: "currentColor" }} />
          </IconButton>
        ),
      },
    ],
    [handleOpenRowMenu, navigate]
  );

  const defaultColumns = useMemo(
    () => [
      {
        id: "name",
        label: "Company Name",
        minWidth: 180,
        width: 180,
        sortable: true,
        renderCell: (row) => <CompanyTableCell row={row} companyEntity onClick={() => openCompany(row)}><InlineBackgroundTaskLabel entity={row} /></CompanyTableCell>,
      },
      {
        id: "status",
        label: "Status",
        minWidth: 92,
        width: 92,
        sortable: true,
        renderCell: (row) => (
          <Chip
            label={row.status || "New"}
            size="small"
            sx={getCompanyStatusBadgeSx(row.status)}
          />
        ),
      },
      {
        id: "contactsCount",
        label: "Contacts",
        minWidth: 66,
        width: 66,
        sortable: true,
        renderCell: (row) => {
          const count = Number(row.contactsCount ?? row.contacts_count ?? 0);

          return (
            <Typography
              variant="body2"
              noWrap
              onClick={count > 0 ? () => handleOpenCompanyContacts(row) : undefined}
              sx={{
                fontWeight: 600,
                color: count > 0 ? "#7C3AED" : "#667085",
                cursor: count > 0 ? "pointer" : "default",
                textAlign: "center",
              }}
            >
              {count}
            </Typography>
          );
        },
      },
      {
        id: "leadsCount",
        label: "Leads",
        minWidth: 58,
        width: 58,
        sortable: true,
        renderCell: (row) => {
          const count = Number(row.leadsCount ?? row.leads_count ?? 0);
          return (
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: 600, color: "#344054", textAlign: "center" }}
            >
              {count}
            </Typography>
          );
        },
      },
      {
        id: "jobOpenings",
        label: "Open Jobs",
        minWidth: 64,
        width: 64,
        sortable: true,
        renderCell: (row) => {
          const count = Number(row.jobsCount ?? row.jobs_count ?? 0);

          return (
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: 600, color: "#344054", textAlign: "center" }}
            >
              {count}
            </Typography>
          );
        },
      },
      {
        id: "location",
        label: "Location",
        minWidth: 112,
        width: 112,
        sortable: true,
        renderCell: (row) => <TableLocationValue row={row} />,
      },
      {
        id: "industry",
        label: "Industry",
        minWidth: 92,
        width: 92,
        sortable: true,
        renderCell: (row) => (
          <Typography sx={{ fontSize: 14, color: "#475467" }}>
            {row.industry || "-"}
          </Typography>
        ),
      },
      {
        id: "contact_information",
        label: "Contact Information",
        minWidth: 124,
        width: 124,
        sortable: false,
        renderCell: (row) => (
          <InfoActionIcons
            showDisabled={true}
            showGroupDivider={false}
            actions={buildTableInfoActions(row.raw || row)}
          />
        ),
      },
      {
        id: "client_portal",
        label: "Client Portal",
        minWidth: 88,
        width: 88,
        sortable: false,
        renderCell: (row) => {
          const portalBadge = getClientPortalBadgeProps(row.status);
          return <Chip label={portalBadge.label} size="small" sx={portalBadge.sx} />;
        },
      },
      {
        id: "lastActivityAt",
        label: "Last Activity",
        minWidth: 90,
        width: 90,
        sortable: true,
        renderCell: (row) => row.lastActivity || "-",
      },
      {
        id: "owner",
        label: "Owner",
        minWidth: 110,
        width: 110,
        sortable: true,
        renderCell: (row) => {
          const ownerName = cleanFilterValue(row.owner || row.owner_name || row.team_member);

          if (!ownerName || ownerName === "-") return "-";

          return (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: 11,
                  fontWeight: 700,
                  backgroundColor: "#EEF2FF",
                  color: "#4338CA",
                }}
              >
                {getPersonInitials(ownerName)}
              </Avatar>
              <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                {ownerName}
              </Typography>
            </Stack>
          );
        },
      },
      {
        id: "assigned_team_members",
        label: "Assigned Team Members",
        minWidth: 96,
        width: 96,
        sortable: false,
        renderCell: (row) => {
          const assignedTeamMembers = buildAssignedTeamMembers(row, teamDirectoryLookup);

          if (!assignedTeamMembers.length) return "-";

          const visibleMembers = assignedTeamMembers.slice(0, 2);
          const overflowCount = Math.max(0, assignedTeamMembers.length - visibleMembers.length);

          return (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              title={assignedTeamMembers.map((member) => member.name).join(", ")}
              sx={{ minWidth: 0 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                {visibleMembers.map((member, index) => (
                  <Avatar
                    key={member.key}
                    src={member.avatarUrl || undefined}
                    sx={{
                      width: 22,
                      height: 22,
                      ml: index === 0 ? 0 : -0.75,
                      border: "1.5px solid #fff",
                      fontSize: 9,
                      fontWeight: 700,
                      backgroundColor: "#EDE9FE",
                      color: "#6D28D9",
                      zIndex: visibleMembers.length - index,
                    }}
                  >
                    {getPersonInitials(member.name)}
                  </Avatar>
                ))}
              </Box>
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                sx={{ color: "#475467", fontSize: 14 }}
              >
                {overflowCount > 0
                  ? `${getPersonInitials(visibleMembers[0]?.name)} +${overflowCount}`
                  : getPersonInitials(visibleMembers[0]?.name)}
              </Typography>
            </Stack>
          );
        },
      },
      {
        id: "actions",
        label: "Actions",
        minWidth: 48,
        width: 48,
        sortable: false,
        renderCell: (row) => (
          <IconButton
            size="small"
            onClick={(event) => handleOpenRowMenu(event, row)}
            sx={{ color: "#667085" }}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        ),
      },
    ],
    [handleOpenCompanyContacts, handleOpenRowMenu, openCompany, teamDirectoryLookup]
  );

  const columns = showingContacts ? contactColumns : defaultColumns;
  const rowMenuIsContact = rowMenuCompany?.entityType === "contact";
  const detailCompanyId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("companyId");
  }, [location.search]);
  const shouldRenderDetailView =
    !sidebarResetToken && (Boolean(detailCompanyId) || (viewMode === "leadTabsLayout" && Boolean(selectedCompany)));
  const detailBreadcrumbLabel = "Companies";
  const shouldRenderLeadDetailView =
    shouldRenderDetailView && String(selectedCompanyEntityType || "").toLowerCase() === "lead";
  const [detailLeadResolvedTitle, setDetailLeadResolvedTitle] = useState("");
  const detailLeadBreadcrumbLabel =
    detailLeadResolvedTitle ||
    selectedCompany?.job_title ||
    selectedCompany?.jobTitle ||
    selectedCompany?.title ||
    selectedCompany?.contact_name ||
    selectedCompany?.name ||
    selectedCompany?.company_name ||
    "Lead";
  const detailParentBreadcrumbLabel =
    selectedCompanyParent?.name || selectedCompanyParent?.company_name || "";
  const detailParentTabBreadcrumbLabel = useMemo(
    () => findTabLabel(COMPANY_PROFILE_TABS, selectedCompanyParentTab),
    [selectedCompanyParentTab]
  );
  const [, setActiveLeadDetailTab] = useState("details");

  useEffect(() => {
    setDetailLeadResolvedTitle("");
  }, [selectedCompany?.id]);

  useEffect(() => {
    if (!shouldRenderLeadDetailView) return;
    setActiveLeadDetailTab("details");
  }, [selectedCompany?.id, shouldRenderLeadDetailView]);

  const renderRow = (row) => (
    <>
      {columns.map((column) => (
        <TableCell
          key={column.id}
          align={column.align || "left"}
          sx={{
            minWidth: column.minWidth,
            width: column.width,
            maxWidth: column.width || column.minWidth,
            overflow: "hidden",
          }}
        >
          {column.renderCell ? column.renderCell(row) : row[column.id]}
        </TableCell>
      ))}
    </>
  );

  // Full page company profile with tabs (keep exact behavior)
  if (shouldRenderDetailView) {
    if (!selectedCompany) {
      return (
        <Box sx={{ p: 3 }}>
          {companyRouteError ? (
            <Alert severity="error" action={<Button onClick={() => setCompanyRouteRetry(value => value + 1)}>Retry</Button>}>
              {companyRouteError}
            </Alert>
          ) : (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={18} />
              <Typography sx={{ fontWeight: 700 }}>Loading company profile...</Typography>
            </Stack>
          )}
        </Box>
      );
    }

    if (shouldRenderLeadDetailView) {
      return (
        <Box sx={{ py: 0, pb: 3, border: 0 }}>
          <Breadcrumb
            activeLabel={detailLeadBreadcrumbLabel}
            appendActiveLabel
            items={[
              { label: "Overview", onClick: () => navigate("/overview") },
              { label: detailBreadcrumbLabel, path: "/companies" },
              ...(detailParentBreadcrumbLabel
                ? [{ label: detailParentBreadcrumbLabel, onClick: handleOpenParentCompanyFromLeadDetail }]
                : []),
              ...(detailParentBreadcrumbLabel && detailParentTabBreadcrumbLabel
                ? [{ label: detailParentTabBreadcrumbLabel, onClick: handleOpenParentCompanyFromLeadDetail }]
                : []),
            ]}
          />

          <Box>
            <LeadDetailsView
              key={String(selectedCompany?.id || detailCompanyId || "")}
              lead={selectedCompany}
              detailFieldOptions={leadDetailFieldOptions}
              initialTab="details"
              onTabChange={setActiveLeadDetailTab}
              onResolvedTitleChange={setDetailLeadResolvedTitle}
            />
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ py: 0, pb: 3, border: 0 }}>
        <Box>
          <LeadTabsLayout
            company={selectedCompany}
            entityType={selectedCompanyEntityType}
            detailFieldOptions={companyDetailFieldOptions}
            onCompanyChange={handleSelectedCompanyChange}
            onTabChange={handleSelectedCompanyTabChange}
            onOpenLeadDetails={handleOpenLeadDetailFromClientView}
            initialTab={profileTab}
            backLabel={detailBreadcrumbLabel}
            quickActionsVariant={returnListTab === LIST_TABS.clients ? "client" : "default"}
            onHeaderEdit={handleOpenEditCompanyDialog}
            onBack={closeSelectedCompanyView}
          />
        </Box>
      </Box>
    );
  }

  const pageTitle = showingContacts ? "Contacts" : showingCompanies ? "Companies" : "Clients";
  const pageBreadcrumbActiveLabel = showingContacts ? "Contacts" : "Companies";
  const searchPlaceholder = showingContacts ? "Search contacts..." : showingCompanies ? "Search companies..." : "Search clients...";

  const topBarActions = [
    {
      key: "filter",
      label: "Filter by",
      variant: "contained",
      startIcon: <Filter size="16" color="currentColor" variant="Linear" />,
      onClick: () => setIsFilterOpen(true),
    },
    {
      key: "import-csv",
      label: "Import CSV",
      variant: "contained",
      onClick: () => setCsvImportOpen(true),
    },
    {
      key: "add-company",
      label: "Add Company",
      variant: "contained",
      startIcon: <ApartmentOutlinedIcon sx={{ fontSize: 18 }} />,
      onClick: openAddCompanyDialog,
    },
    {
      key: "add-contact",
      label: "Add Contact",
      variant: "contained",
      startIcon: <PermContactCalendarOutlinedIcon sx={{ fontSize: 18 }} />,
      onClick: handleOpenAddContactAction,
    },
    {
      key: "add-lead",
      label: "Add Lead",
      variant: "contained",
      startIcon: <LeaderboardOutlinedIcon sx={{ fontSize: 18 }} />,
      onClick: handleOpenAddLeadAction,
    },
    {
      key: "add-agent",
      label: "Add Agent",
      variant: "contained",
      startIcon: <BoltOutlinedIcon sx={{ fontSize: 18 }} />,
      onClick: handleOpenTopBarCompanyAgent,
    },
  ];

  const companyBulkRightButtons = [
    {
      key: "companies-add-note",
      label: "Add Note",
      background: BULK_ACTION_RIGHT_BUTTON_COLOR,
      color: "#fff",
      onClick: handleOpenAddNoteAction,
      sx: BULK_ACTION_RIGHT_BUTTON_SX,
    },
    {
      key: "companies-create-proposal",
      label: "Create Proposal",
      startIcon: <ProposalActionIcon size={18} />,
      background: BULK_ACTION_RIGHT_BUTTON_COLOR,
      color: "#fff",
      onClick: handleCreateCompanyProposal,
      sx: BULK_ACTION_RIGHT_BUTTON_SX,
    },
    {
      key: "companies-launch-agent",
      label: "Launch Agent",
      requiresSelection: false,
      
      background: BULK_ACTION_RIGHT_BUTTON_COLOR,
      color: "#fff",
      onClick: handleLaunchCompanyAgent,
      sx: BULK_ACTION_RIGHT_BUTTON_SX,
    },
  ];

  const bulkActionsNode = (
    <BulkActionsBar
      selectedCount={selectedCount}
      selectionLabel={showingContacts ? "contact" : "company"}
      actions={
        showingContacts
          ? []
          : [
              {
                type: "menu",
                label: statusSaving ? "Saving..." : "Change Status",
                options: activeStatusOptions,
                onSelect: handleApplyStatus,
                feedback: statusToast,
                onFeedbackClose: () =>
                  setStatusToast((prev) => ({ ...prev, open: false })),
                disabledMessage:
                  !selectedCount && !statusSaving ? "Please select an item to change status." : "",
                disabled: statusSaving || !selectedCount,
              },
              { type: "divider", sx: { width: "1.5px" } },
              {
                type: "node",
                key: "assign-owner",
                node: (
                  <AssignTeamMemberAction
                    apiBase={API_BASE}
                    selectedCount={selectedCount}
                    label="Assign Owner"
                    optionsMode="recruiterUsers"
                    assignmentRoleLabel="owner"
                    onAssign={handleAssignOwner}
                  />
                ),
              },
              { type: "divider" },
              {
                type: "node",
                key: "assign-team-member",
                node: (
                  <AssignTeamMemberAction
                    apiBase={API_BASE}
                    selectedCount={selectedCount}
                    label="Assign Team Member"
                    optionsMode="teamRoster"
                    assignmentRoleLabel="team member"
                    emptySelectionMessage="Please select at least one company to assign a team member."
                    onAssign={handleAssignTeamMember}
                  />
                ),
              },
              { type: "divider" },
              {
                type: "button",
                label: "Add to Sequence",
                onClick: handleOpenAddToSequence,
                disabled: !selectedCount,
              },
              { type: "divider" },
              {
                type: "menu",
                label: "Launch Agent",
                options: COMPANY_AGENT_TASK_OPTIONS,
                onSelect: handleLaunchAgentTask,
                confirmLabel: "Launch",
              },
              { type: "divider" },
              {
                type: "button",
                label: "Export CSV",
                onClick: handleExportSelected,
              },
              { type: "divider" },
              {
                type: "button",
                label: "Archive",
                onClick: handleArchiveSelected,
                confirmLabel: "Archive",
                disabled: statusSaving || !selectedCount,
              },
              isAdminUser ? { type: "divider" } : null,
              isAdminUser
                ? {
                    type: "button",
                    label: "Delete",
                    onClick: handleBulkDelete,
                    confirmLabel: "Delete",
                    confirmMessage: ({ selectedCount: nextSelectedCount = 0 }) =>
                      `Delete ${nextSelectedCount || 0} selected ${nextSelectedCount === 1 ? "item" : "items"}? This cannot be undone.`,
                    disabled: !selectedCount,
                  }
                : null,
            ].filter(Boolean)
      }
      rightButtons={companyBulkRightButtons}
    />
  );

  if (showAdvancedSearch) {
    return (
      <Box>
        <Breadcrumb
          items={[
            { label: "Overview", onClick: () => navigate("/overview") },
            { label: "Companies", path: "/companies" },
            { label: "Advance Search" },
          ]}
        />

        <Box>
          <CompanyAdvancedSearchView
            title="Companies - Advance Search"
            initialFilters={advancedApplied || undefined}
            options={companyAdvancedSearchOptions}
            onClose={() => setShowAdvancedSearch(false)}
            onApply={(adv) => {
              setAdvancedApplied(adv);
              setCompanyTablePage(0);
              setShowAdvancedSearch(false);
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 0, pb: 3, border: 0 }}>
      <ListTableTabsTemplate
        breadcrumb={
          <Breadcrumb
            activeLabel={pageBreadcrumbActiveLabel}
            items={[
              { label: "Overview", onClick: () => navigate("/overview") },
              { label: "Companies", path: "/companies" },
            ]}
          />
        }
        statsSection={
          <StatsSlider
            items={topStats}
            autoPlay={false}
            showArrows={true}
            cardMinWidth={200}
            gap={12}
            onItemClick={handleStatItemClick}
            activeItemId={activeStatId}
          />
        }
        contentSection={
          <>
            <Card
              sx={{
                p: 0,
                backgroundColor: "#fff",
                boxShadow: "none",
                overflow: "hidden",
              }}
            >
              <TopListHeaderBar
                title={pageTitle}
                searchPlaceholder={searchPlaceholder}
                searchValue={search}
                onSearchChange={(e) => {
                  setSearch(e.target.value);
                  setCompanyTablePage(0);
                  setContactTablePage(0);
                }}
                actions={topBarActions}
                resultsToolbarTargetId="clients-results-toolbar"
                tabContent={
                  <Box
                    sx={{
                      px: 0,
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <TabNavigationBar
                      value={listTab}
                      onChange={(nextValue) => {
                        setListTab(nextValue);
                        setCompanyTablePage(0);
                        setContactTablePage(0);
                      }}
                      items={[
                        {
                          value: LIST_TABS.clients,
                          label: "Clients",
                          count: clientRowsTotal,
                        },
                        {
                          value: LIST_TABS.companies,
                          label: "Companies",
                          count: companyRowsTotal,
                        },
                        {
                          value: LIST_TABS.contacts,
                          label: "Contacts",
                          // This is the tab's database total, not the size of the
                          // currently loaded server page (for example, 10 rows).
                          count: contactRowsTotal ?? enrichedContactRows.length,
                        },
                      ]}
                      borderBottom={false}
                    />
                  </Box>
                }
              />

              <Box
                onChangeCapture={(e) => {
                  if (showingCompanies || showingContacts) return;

                  const el = e.target;
                  if (!el || el.tagName?.toLowerCase() !== "input") return;
                  if (el.type !== "checkbox") return;

                  const inThead = el.closest("thead");
                  if (inThead) {
                    if (el.checked) setSelectedIdsLocal(filteredRows.map((r) => r.id));
                    else setSelectedIdsLocal([]);
                    return;
                  }

                  const tr = el.closest("tr");
                  const rawId = tr?.getAttribute("data-id") || tr?.dataset?.id;
                  const id = normalizeId(rawId);
                  if (id === null) return;

                  setSelectedIdsLocal((prev) => {
                    const set = new Set((prev || []).map((x) => String(x)));
                    if (el.checked) set.add(String(id));
                    else set.delete(String(id));
                    return Array.from(set).map((x) => normalizeId(x));
                  });
                }}
              >
                <Box>
                  <GenericDataTable
                    columns={columns}
                    exportFields={[...defaultColumns, ...contactColumns, ...COMPANY_EXPORT_TAB_FIELDS].filter((field) => field.id !== "actions")}
                    rows={filteredRows}
                    renderRow={renderRow}
                    defaultOrderBy={showingContacts ? "updated_at" : "lastActivityAt"}
                    defaultOrderDirection="desc"
                    resetPageSignal={tableResetSignal}
                    includeSelectionColumn
                    fitToContainer
                    resultsToolbarTargetId="clients-results-toolbar"
                    resultsFilterPills={showingContacts ? contactFilterPills(contactFilters, next => { setContactFilters(next); setContactTablePage(0); }) : resultsFilterPills}
                    selectedIds={selectedIds}
                    onSelectedIdsChange={setSelectedIds}
                    resultsTotalCount={
                      contactsUseServerPagination
                        ? contactRowsTotal
                        : !showingContacts && !shouldExpandCompanyRows
                          ? (showingCompanies ? companyRowsTotal : clientRowsTotal)
                          : undefined
                    }
                    serverSidePagination={
                      contactsUseServerPagination ||
                      (!showingContacts && !shouldExpandCompanyRows)
                    }
                    serverPage={showingContacts ? contactTablePage : companyTablePage}
                    serverRowsPerPage={showingContacts ? contactTableRowsPerPage : companyTableRowsPerPage}
                    onServerPageChange={showingContacts ? handleContactServerPageChange : handleCompanyServerPageChange}
                    onServerRowsPerPageChange={showingContacts ? handleContactServerRowsPerPageChange : handleCompanyServerRowsPerPageChange}
                    visualVariant="company-standard"
                    tableSx={
                      !showingContacts
                        ? {
                            width: "100%",
                            tableLayout: "fixed",
                            "& .MuiTableHead-root .MuiTableCell-root": {
                              px: 0.75,
                              py: 0.85,
                              whiteSpace: "normal",
                              lineHeight: 1.15,
                              fontSize: 14,
                              verticalAlign: "middle",
                            },
                            "& .MuiTableHead-root .MuiTableSortLabel-root": {
                              whiteSpace: "normal",
                              lineHeight: 1.15,
                            },
                            "& .MuiTableBody-root .MuiTableCell-root": {
                              px: 0.75,
                              py: 0.9,
                            },
                          }
                        : undefined
                    }
                    bulkActionsTop={bulkActionsNode}
                    bulkActionsBottom={bulkActionsNode}
                  />
                </Box>
              </Box>
            </Card>

            <CompanyProfileDrawer
              open={companyDrawerOpen}
              onClose={() => setCompanyDrawerOpen(false)}
              company={selectedCompany}
              entityType={selectedCompanyEntityType}
              allowShowMore={true}
              onShowMore={handleShowMoreCompany}
            />

            <CompanyFilterDrawer
              open={isFilterOpen && !showingContacts}
              onClose={() => setIsFilterOpen(false)}
              rows={companyRows}
              endpoint={`/api/companies?${new URLSearchParams({scope:listTab === LIST_TABS.clients ? "client" : "company", search:search.trim(), stats_filter:activeStatId || ""})}`}
              value={filters}
              onApply={(next) => {
                setFilters(normalizeFilters(next));
                setCompanyTablePage(0);
                setContactTablePage(0);
                setIsFilterOpen(false);
              }}
              onClear={() => {
                setFilters(DEFAULT_FILTERS);
                setCompanyTablePage(0);
                setContactTablePage(0);
              }}
              onAdvancedSearch={(drawerFilters) => {
                setIsFilterOpen(false);
                setShowAdvancedSearch(true);
                setFilters(normalizeFilters(drawerFilters));
                setCompanyTablePage(0);
                setContactTablePage(0);
              }}
            />

            <ContactFiltersDrawer open={isFilterOpen && showingContacts} value={contactFilters}
              onClose={() => setIsFilterOpen(false)}
              onApply={next => { setContactFilters(next); setContactTablePage(0); }} />

            <Snackbar open={toast.open} autoHideDuration={2500} onClose={() => setToast((t) => ({ ...t, open: false }))}>
              <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ width: "100%" }}>
                {toast.msg}
              </Alert>
            </Snackbar>

            <LeadDescriptionDialog
              open={companyLeadDialogOpen}
              onClose={handleCloseCompanyLeadDialog}
              value={companyLeadDialogValue}
              onChange={setCompanyLeadDialogValue}
              mode={companyLeadDialogMode}
              initialScreen="start"
              onSubmit={handleCompanyLeadDialogSubmit}
            />

            <Menu
              anchorEl={rowMenuAnchorEl}
              open={Boolean(rowMenuAnchorEl)}
              onClose={handleCloseRowMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  mt: 0.5,
                  minWidth: 210,
                  borderRadius: 2.5,
                  boxShadow: "0 16px 40px rgba(15,23,42,0.16)",
                },
              }}
            >
              <MenuItem onClick={rowMenuIsContact ? handleViewContactProfileAction : handleViewCompanyProfileAction} sx={{ fontSize: 13 }}>
                {rowMenuIsContact ? "View Profile" : "View Company Profile"}
              </MenuItem>
              <MenuItem onClick={rowMenuIsContact ? handleShareContactAction : handleShareCompanyAction} sx={{ fontSize: 13 }}>
                Share Link
              </MenuItem>
              <MenuItem onClick={rowMenuIsContact ? handleEditContactAction : handleOpenEditCompanyDialog} sx={{ fontSize: 13 }}>
                {rowMenuIsContact ? "Edit" : "Edit Company"}
              </MenuItem>
              <MenuItem onClick={rowMenuIsContact ? handleAddContactNoteAction : handleAddCompanyNoteAction} sx={{ fontSize: 13 }}>
                Add Notes
              </MenuItem>
              <MenuItem onClick={rowMenuIsContact ? handleAddContactTagsAction : handleAddCompanyTagsAction} sx={{ fontSize: 13 }}>
                Add Tags
              </MenuItem>
              <MenuItem
                onClick={rowMenuIsContact ? handleArchiveContactAction : handleArchiveCompanyAction}
                sx={{ fontSize: 13, color: "#B42318" }}
              >
                {rowMenuIsContact ? "Archive" : "Archive Company"}
              </MenuItem>
            </Menu>

            <Dialog
              open={addContactOpen}
              onClose={closeAddContactDialog}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle data-popup-header sx={{ fontSize: 18, fontWeight: 700 }}>
                Add Contact
              </DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  {addContactError ? (
                    <Alert severity="error">{addContactError}</Alert>
                  ) : null}
                  <Typography sx={{ fontSize: 12, color: "#667085" }}>
                    {`Company: ${
                      cleanFilterValue(
                        addContactTarget?.name ||
                          addContactTarget?.company_name ||
                          addContactTarget?.raw?.company_name
                      ) || "Selected Company"
                    }`}
                  </Typography>
                  <TextField
                    label="Contact Name"
                    value={addContactForm.name}
                    onChange={handleAddContactFieldChange("name")}
                    size="small"
                    fullWidth
                    required
                  />
                  <TextField
                    label="Role"
                    value={addContactForm.role}
                    onChange={handleAddContactFieldChange("role")}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Email"
                    value={addContactForm.email}
                    onChange={handleAddContactFieldChange("email")}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Phone"
                    value={addContactForm.phone}
                    onChange={handleAddContactFieldChange("phone")}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="LinkedIn URL"
                    value={addContactForm.linkedin_url}
                    onChange={handleAddContactFieldChange("linkedin_url")}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Source"
                    value={addContactForm.source}
                    onChange={handleAddContactFieldChange("source")}
                    size="small"
                    fullWidth
                  />
                </Stack>
              </DialogContent>
              <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}>
                <Button onClick={closeAddContactDialog} disabled={addContactSaving}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveAddContact}
                  disabled={addContactSaving}
                  sx={{
                    textTransform: "none",
                    borderRadius: "999px",
                    backgroundColor: BULK_ACTION_RIGHT_BUTTON_COLOR,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: BULK_ACTION_RIGHT_BUTTON_COLOR,
                      boxShadow: "none",
                    },
                  }}
                >
                  {addContactSaving ? "Saving..." : "Save Contact"}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={addNoteOpen}
              onClose={closeAddNoteDialog}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle data-popup-header sx={{ fontSize: 18, fontWeight: 700 }}>
                Add Note
              </DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  {addNoteError ? <Alert severity="error">{addNoteError}</Alert> : null}
                  <TextField
                    label="Title"
                    value={addNoteTitle}
                    onChange={(event) => setAddNoteTitle(event.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Note"
                    value={addNoteBody}
                    onChange={(event) => setAddNoteBody(event.target.value)}
                    size="small"
                    multiline
                    minRows={5}
                    fullWidth
                  />
                </Stack>
              </DialogContent>
              <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}>
                <Button onClick={closeAddNoteDialog} disabled={addNoteSaving}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveAddNote}
                  disabled={addNoteSaving}
                  sx={{
                    textTransform: "none",
                    borderRadius: "999px",
                    backgroundColor: BULK_ACTION_RIGHT_BUTTON_COLOR,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: BULK_ACTION_RIGHT_BUTTON_COLOR,
                      boxShadow: "none",
                    },
                  }}
                >
                  {addNoteSaving ? "Saving..." : "Save Note"}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={editCompanyOpen}
              onClose={closeEditCompanyDialog}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle data-popup-header sx={{ fontSize: 18, fontWeight: 700 }}>
                Edit Company
              </DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  {editCompanyError ? <Alert severity="error">{editCompanyError}</Alert> : null}
                  <TextField
                    label="Company Name"
                    value={editCompanyForm.name}
                    onChange={handleEditCompanyFieldChange("name")}
                    size="small"
                    fullWidth
                    required
                  />
                  <TextField
                    label="Website"
                    value={editCompanyForm.website}
                    onChange={handleEditCompanyFieldChange("website")}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Location"
                    value={editCompanyForm.location}
                    onChange={handleEditCompanyFieldChange("location")}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Industry"
                    value={editCompanyForm.industry}
                    onChange={handleEditCompanyFieldChange("industry")}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    select
                    label="Status"
                    value={editCompanyForm.status}
                    onChange={handleEditCompanyFieldChange("status")}
                    size="small"
                    fullWidth
                  >
                    {COMPANY_STATUS_OPTIONS.map((statusOption) => (
                      <MenuItem key={statusOption} value={statusOption}>
                        {statusOption}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </DialogContent>
              <DialogActions data-popup-footer sx={{ px: 3, pb: 2 }}>
                <Button onClick={closeEditCompanyDialog} disabled={editCompanySaving}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveEditCompany}
                  disabled={editCompanySaving}
                  sx={{
                    textTransform: "none",
                    borderRadius: "999px",
                    backgroundColor: BULK_ACTION_RIGHT_BUTTON_COLOR,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: BULK_ACTION_RIGHT_BUTTON_COLOR,
                      boxShadow: "none",
                    },
                  }}
                >
                  {editCompanySaving ? "Saving..." : "Save Changes"}
                </Button>
              </DialogActions>
            </Dialog>

            <AddJobDialog
              open={addJobOpen}
              onClose={() => setAddJobOpen(false)}
              onCreated={() => setReloadKey((prev) => prev + 1)}
              user={user}
            />

            <AddCompanyDialog
              open={addClientOpen}
              onClose={() => setAddClientOpen(false)}
              user={user}
              initialValues={companyDialogInitialValues}
              onCreated={() => setReloadKey((prev) => prev + 1)}
            />
            <CsvImportDialog open={csvImportOpen} onClose={() => setCsvImportOpen(false)} entityLabel={showingContacts ? "contacts" : showingCompanies ? "companies" : "clients"} importEndpoint={`${API_BASE}/api/csv-import/${showingContacts ? "contacts" : showingCompanies ? "companies" : "companies?scope=client"}`} authHeaders={authHeaders} onImported={() => setReloadKey((value) => value + 1)} />

            <AddToSequenceDialog
              open={isAddToSequenceOpen}
              onClose={() => setIsAddToSequenceOpen(false)}
              entityType="client"
              items={selectedSequenceItems}
              onCreated={() => {
                setSelectedIds([]);
                setSelectedIdsLocal([]);
                setReloadKey((prev) => prev + 1);
              }}
            />

            <AIAgentDialog
              open={isAgentDialogOpen}
              onClose={handleCloseAgentDialog}
              user={user}
              initialPrompt={agentDialogInitialPrompt}
              submitOnOpen={agentDialogSubmitOnOpen}
            />
          </>
        }
      />
    </Box>
  );
}

const Clients = () => {
  const { user, loading } = useAuthController();

  if (loading) return <p>Loading...</p>;
  if (user?.role === "candidate") return null;

  return <ClientsContent user={user} />;
};

export default Clients;
